/**
 * Session Store
 * 
 * Manages prioritization sessions including:
 * - Session lifecycle (DRAFT → CRITERIA_FINALIZED → RUN_COMPLETE → PUBLISHED)
 * - Session-scoped criteria configuration
 * - Item eligibility computation
 * - Tool configuration and results
 * - Session decisions and audit trail
 * 
 * Sessions can be:
 * - Cycle-linked sessions with cycle-execution lineage
 * - Standalone (for ad-hoc prioritization)
 * 
 * SUPABASE-ONLY PERSISTENCE:
 * - Primary: Supabase (single source of truth)
 * - No localStorage cache (eliminates stale data issues)
 * 
 * ============================================================================
 * STATE MACHINE INVARIANTS (Doc 02, Doc 03)
 * ============================================================================
 * 
 * INVARIANT 1: Sessions own execution; tasks never substitute.
 *   - Session state machine: DRAFT → CRITERIA_FINALIZED → RUN_COMPLETE → PUBLISHED
 *   - This is where the actual prioritization work happens
 *   - Tasks are responsibility assignments pointing to Sessions
 * 
 * INVARIANT 2: A session can exist without a task, but not vice-versa.
 *   - Sessions are the canonical execution containers
 *   - Standalone sessions (ad-hoc prioritization) are valid
 *   - Every Task MUST reference exactly one Session
 * 
 * INVARIANT 3: Sessions do not advance cycle states.
 *   - Session state changes (DRAFT → PUBLISHED) are independent of cycle phase
 *   - Session completion does not trigger cycle governance/state transitions
 *   - Only the Cycle state machine (degapProcessStore) controls phases
 * 
 * HISTORICAL NOTE: Before Gap #5 fix, Sessions were sometimes optional
 * for Tasks. This is now canonically invalid per Doc 02.
 */
import { normalizeToolConfig } from '../../lib/toolSettings';
import {
  SITUATION_VECTOR_VERSION,
  isEmptySituationVector,
  type SituationVector,
} from '../../types/prioritization/situationVector';
import { uniqueLines } from '../../lib/uniqueLines';

import { useMemo } from 'react';
import { create } from 'zustand';
import type { RealtimeChannel } from '@supabase/supabase-js';
import {
  getLegacySessionTypeForIntent,
  sessionHasPrioritizationResults,
  getCanonicalSessionTypeForIntent,
  deriveRunMode,
} from '../../types/prioritization/session';
import type {
  PrioritizationSession,
  SessionState,
  SessionCriteriaConfig,
  ItemEligibility,
  ItemEligibilityStatus,
  SessionRankingOutput,
  SessionDecision,
  SessionModeConfig,
  SessionIntent,
  SessionContextMode,
} from '../../types/prioritization/session';
import { appTelemetry } from '../../lib/appTelemetry';
import { getMechanismTagsForToolId } from '../../lib/methodRegistry';
import { validateStageMechanismFit } from '../../lib/stageToolValidator';
import type { ScenarioExchangeState } from '../../lib/stageToolValidator';
import { useAuditStore, type SessionEvent } from './auditStore';
import { useSessionReportStore } from './sessionReportStore';
import { useItemsStore } from './itemsStore';
import { useToolSessionStore } from './toolSessionStore';
import type { Item } from '../../types/prioritization';
import { computeViolations } from '../../lib/poolViolations';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../lib/featureFlags';
import { fetchSessionDefaults, type SessionDefaults } from '../../lib/sessionDefaults';
import { useItemPoolsStore } from './itemPoolsStore';
import { useValueProposalStore } from './valueProposalStore';
import { useAttributesStore } from './attributesStore';
import {
  fetchAll,
  fetchById,
  insertRecord,
  updateRecord,
  isSupabaseAvailable,
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  toDbFormat,
  fromDbFormat,
  createInitialSyncState,
  generateUUID,
  type SyncState,
} from '../../lib/supabaseSync';
import { checkPolicyForSession } from '../../lib/authorization/storeEnforcement';
import { useTenantStore } from './tenantStore';
import { useSpaceStore } from './spaceStore';
import { useAttributeGovernanceStore } from './attributeGovernanceStore';
import { useDecisionLoopStore } from './decisionLoopStore';
import { 
  evaluateToolCompatibility,
  type ToolCompatibilityContext,
} from '../../lib/toolCompatibilityEvaluator';
import { normalizeSessionToolId, sessionHasObviousOrLockedCriteria } from '../../lib/sessionToolCatalog';
import { computeValueSufficiency } from '../../lib/valueSufficiency';
import { setSessionCutLine } from '../../lib/governedTransactions';
import type { RationaleParts } from '../../lib/rationaleParts';
import { useUserStore } from './userStore';
import { itemChangeRule, type ItemChangeRule } from '../../lib/workspaceSessionRules';
import { getTrackRelationshipBlockerMessage, summarizeTrackRelationshipReadiness } from '../../lib/trackRelationshipReview';
import {
  retireSession as retireSessionRpc,
  restoreRetiredSession as restoreRetiredSessionRpc,
  type ReversalResult,
} from '../../lib/reversals/reversalsService';

/** A session retired through retire_session (mig 758) — hidden from every list. */
export function isSessionRetired(session: Pick<PrioritizationSession, 'retiredAt'>): boolean {
  return session.retiredAt !== undefined && session.retiredAt !== null && session.retiredAt !== '';
}

const TABLE_NAME = 'prioritization_sessions';

/** Prefer newer row on updatedAt, but never drop durable results from the DB copy. */
function mergeSessionFromDb(
  local: PrioritizationSession,
  dbSession: PrioritizationSession
): PrioritizationSession {
  const localUpdated = local.updatedAt instanceof Date ? local.updatedAt.getTime() : new Date(local.updatedAt).getTime();
  const dbUpdated = dbSession.updatedAt instanceof Date ? dbSession.updatedAt.getTime() : new Date(dbSession.updatedAt).getTime();
  const newer = localUpdated >= dbUpdated ? local : dbSession;
  const older = localUpdated >= dbUpdated ? dbSession : local;
  const rankingOutput = newer.rankingOutput ?? older.rankingOutput;
  const runCompletedAt = newer.runCompletedAt ?? older.runCompletedAt;
  const state =
    sessionHasPrioritizationResults(older) && !sessionHasPrioritizationResults(newer)
      ? older.state
      : newer.state;
  return {
    ...newer,
    rankingOutput,
    runCompletedAt,
    state,
  };
}

// ─── Session Realtime Event (Brief F) ────────────────────────────────────────
// Distinct from auditStore's SessionEvent; represents rows in session_events table.

/** Event types that only the session facilitator may write. Brief F §2. */
const FACILITATOR_ONLY_EVENTS = new Set([
  'item_moved',
  'item_added',
  'item_removed',
  'session_phase_advanced',
  'result_locked',
]);

interface SessionRealtimeEvent {
  id: string;
  session_id: string;
  workspace_id: string;
  actor_id: string;
  actor_name: string;
  event_type: string;
  payload: Record<string, unknown>;
  created_at: string;
}

/** Map session intent to decision class (for loop gating). */
const INTENT_TO_DECISION_CLASS: Record<SessionIntent, string> = {
  select_attributes: 'attribute_selection',
  converge_values: 'values_choice_selection',
  select_item_pool: 'item_selection',
  rank_priorities: 'item_prioritization',
  calibration: 'item_selection',
  review: 'item_selection',
};

/** Decision classes that must be COMPLETE before starting item_prioritization. */
const LOOPS_REQUIRED_BEFORE_ITEM_PRIORITIZATION: string[] = [
  'item_selection',
  'attribute_selection',
  'values_choice_selection',
  'value_selection',
];

// ============================================================================
// Store State Type
// ============================================================================

/** What adding one item to a session pool did, once the server answered. */
export type SessionItemAddOutcome = { ok: true; alreadyIn?: boolean } | { ok: false; reason: string };

interface SessionStoreState {
  /** Live sessions. Retired sessions (mig 758) are held apart in `retiredSessions`. */
  sessions: PrioritizationSession[];
  /**
   * Sessions retired through retire_session: hidden from every list, kept so
   * the session page can say so and offer Take back.
   */
  retiredSessions: PrioritizationSession[];
  activeSessionId: string | null;

  /**
   * Human-readable reason the most recent createSession() call failed (returned
   * undefined), or null on success. Callers that only observe the undefined
   * return (through createSessionWithFeedback) read this to show the *actual* cause instead
   * of a generic/misleading message. Cleared at the start of each createSession.
   */
  lastCreateError: string | null;

  /**
   * Human-readable note from a "warn-and-proceed" gate the most recent
   * createSession() call passed through (e.g. an incomplete decision loop,
   * unreviewed track relationships) — session creation still succeeded, but
   * the caller should surface this as a non-blocking notice rather than only
   * the console.warn it was previously silent to the user. Null when the
   * last successful create had no such note. Cleared at the start of each
   * createSession.
   */
  lastCreateWarning: string | null;

  // Sync state
  syncState: SyncState;
  
  // Supabase sync actions
  fetchFromSupabase: () => Promise<void>;
  /** Fetch a single session by ID and merge into store (e.g. for direct link when not in workspace list). */
  fetchSessionById: (sessionId: string) => Promise<boolean>;
  syncToSupabase: (
    session: PrioritizationSession,
    options?: { includeState?: boolean },
  ) => Promise<{ success: boolean; error?: string }>;
  
  // ================================
  // Session CRUD
  // ================================
  
  createSession: (params: {
    name: string;
    description?: string;
    facilitatorId: string;
    createdBy: string;
    cycleId?: string;
    /** 
     * Legacy field name for the required cycle-execution lineage record.
     * Sessions should belong to the execution lineage container, not directly to cycles.
     */
    egapIterationId?: string;
    /**
     * Session context mode. Default cycle_linked when trackId is provided; app_linked when not.
     * cycle_linked requires trackId; app_linked/isolated do not.
     */
    sessionContextMode?: SessionContextMode;
    /**
     * Track this session belongs to. Required when sessionContextMode is cycle_linked.
     * Optional for app_linked/isolated (Stage 2B will make DB column nullable).
     */
    trackId?: string | null;
    /** The Catchball Ball this session is run in response to. Null for non-Catchball sessions. */
    catchballBallId?: string | null;
    modeConfig?: Partial<SessionModeConfig>;
    /** Required: declares session purpose for gate validation */
    intent: 'select_attributes' | 'converge_values' | 'select_item_pool' | 'rank_priorities' | 'calibration' | 'review';
    /**
     * Inherited from track: criteria (attribute keys) so session does not re-ask.
     * When provided, session starts with these criteria and reconciliation required for them.
     */
    initialCriteriaIds?: string[];
    /**
     * Inherited from track: the track's weights for those criteria (migration 1047),
     * by criterion id. Used when every initial criterion has one; otherwise the
     * session starts from an even split.
     */
    initialCriteriaWeights?: Record<string, number>;
    /**
     * Inherited from track: item IDs from the track's pool so session does not re-add items.
     */
    initialItemIds?: string[];
    /**
     * For app_linked sessions: workspace item IDs to include in this session.
     */
    itemIds?: string[];
    /**
     * For isolated sessions: manually entered item labels (not linked to workspace items).
     */
    isolatedItems?: string[];
    /**
     * For session-first creation (Path C): the meta-category the user selected.
     * Stored on the session for filtering and context display.
     */
    selectedMetaCategory?: import('../../types/prioritization/lifecycle').ItemMetaCategory;
    /**
     * The tool that will run in this session (registry toolId, e.g. "dot_voting").
     * When provided at creation time the session opens with the tool already selected,
     * avoiding the "A prioritization tool must be selected" gate failure.
     */
    selectedToolId?: string;
    /**
     * Tool-specific configuration set before the session starts (e.g. dotsPerParticipant
     * for Dot Voting, budgetPercentage for Buy A Feature).
     */
    toolConfig?: Record<string, unknown>;
    /**
     * Declared description of the room, from the setup questions (migration 715).
     * Optional; when omitted or empty the session behaves exactly as before.
     */
    situationVector?: SituationVector | null;
    /**
     * The prioritization context this session belongs to.
     * When provided, the session is linked to the context for condition resolution,
     * stage visibility, and decision unit tracking.
     */
    contextId?: string;
    /** Workspace this session belongs to. Falls back to getCurrentWorkspaceId() when omitted. */
    workspaceId?: string;
    /** Product module that created this session (migration 490). */
    originModule?: import('../../types/prioritization/originModule').OriginModule;
    /**
     * Catchball exchange state at the moment of session creation (Canon 23 §9.3, Gap 4).
     * Recorded in the audit trail so governance history reflects whether this session
     * was responding to a request, a revision, or was fresh work.
     * Defaults to 'not_applicable' when omitted.
     */
    exchangeState?: ScenarioExchangeState;
  }) => Promise<PrioritizationSession | undefined>;
  
  updateSession: (
    sessionId: string,
    updates: Partial<Pick<PrioritizationSession, 'name' | 'description' | 'sessionNotes' | 'scheduledFor' | 'scheduledDuration' | 'modeConfig' | 'toolConfig' | 'selectedGridCells' | 'meetingLink' | 'meetingProvider' | 'nestedPrioritizationEvent' | 'facilitatorId' | 'rankingOutput' | 'cutLinePosition' | 'metadata' | 'state' | 'situationVector' | 'situationVectorVersion'>>
  ) => Promise<{ success: boolean; error?: string }>;
  
  updateModeConfig: (
    sessionId: string,
    modeConfig: Partial<SessionModeConfig>
  ) => Promise<{ success: boolean; error?: string }>;
  
  /**
   * The user-facing "delete": governed retire (retire_session, mig 758). A
   * written reason is required; the server refuses (blockedBy + remedy) once
   * something downstream uses the session. On success the session moves from
   * `sessions` to `retiredSessions`.
   */
  retireSession: (sessionId: string, reason: string) => Promise<ReversalResult>;

  /** Take back a retired session (restore_retired_session, mig 758). */
  restoreRetiredSession: (sessionId: string, reason: string) => Promise<ReversalResult>;

  /**
   * Clone a completed session as a fresh DRAFT re-run.
   * The clone copies setup (participants, items, criteria, tool) and records
   * modeConfig.reRunOf so the result-decision UI can surface on completion.
   * Returns the new session id on success, or null on failure.
   */
  cloneSessionForReRun: (sessionId: string) => Promise<string | null>;

  /**
   * Discard a re-run session and return to the original. The re-run is a
   * throwaway draft the user explicitly abandons before anything downstream
   * has consumed it, so this retires it (Tier 1, canon 11 §2.1) rather than
   * purging it — the row stays and it can be taken back like any other
   * retired session. Only callable when modeConfig.reRunOf is set and state
   * is not PUBLISHED (retire_session also refuses PUBLISHED, but this fails
   * fast with a clearer message since "discard" implies nothing shipped).
   */
  discardReRun: (sessionId: string) => Promise<{ success: boolean; error?: string }>;

  /**
   * Link an existing app_linked session to a Track Instance.
   * If the track is Cycle-Bound, the session is promoted to cycle_linked.
   */
  linkSessionToTrack: (sessionId: string, trackId: string) => Promise<{ success: boolean; error?: string }>;
  
  setActiveSession: (sessionId: string | null) => void;
  
  getSession: (sessionId: string) => PrioritizationSession | undefined;
  
  getActiveSession: () => PrioritizationSession | undefined;
  
  /**
   * Get all sessions for a cycle-execution lineage record.
   * The current runtime field name remains `egapIterationId`.
   */
  getSessionsForIteration: (egapIterationId: string) => PrioritizationSession[];
  
  /**
   * Get sessions for an iteration filtered by intent.
   */
  getSessionsByIntent: (egapIterationId: string, intent: SessionIntent) => PrioritizationSession[];
  
  // ================================
  // Canon Hierarchy Navigation (v2.2)
  // ================================
  
  /**
   * Get the Loop ID for a session by traversing: Session → execution lineage record → CatchballLoop
   * Returns null if session has no egapIterationId or iteration has no catchballLoopId.
   */
  getLoopForSession: (sessionId: string) => Promise<string | null>;
  
  /**
   * Get the Track (ItemPool) for a session by traversing:
   * Session → execution lineage record → CatchballLoop → ItemPool (where isTrack=true)
   * Returns null if any link is missing or pool is not a Track.
   */
  getTrackForSession: (sessionId: string) => Promise<{ poolId: string; itemCategory: string; altitudeLevel: number } | null>;
  
  // ================================
  // Session State Machine
  // ================================
  
  transitionSessionState: (
    sessionId: string,
    targetState: SessionState,
    actorId: string,
    /** `rationale` (985): the four-part rationale written as the criteria are
     *  locked; it lands on the criteria_finalized Decision. */
    options?: { participantCount?: number; rationale?: RationaleParts | null }
  ) => Promise<{ success: boolean; error?: string }>;
  
  canTransitionSessionState: (
    sessionId: string,
    targetState: SessionState,
    options?: { participantCount?: number }
  ) => { allowed: boolean; reason?: string };
  
  // ================================
  // Lock Semantics (v2.2 Canon)
  // ================================
  
  /**
   * Start a session, locking its configuration.
   * Per Canon: "Configuration locks at session start. Post-lock changes require
   * a new session instance."
   * 
   * This function:
   * 1. Sets configurationLockedAt timestamp
   * 2. Creates snapshot references for pool and criteria
   * 3. Blocks future config modifications
   * 
   * @returns Success/error result with snapshot IDs
   */
  startSession: (sessionId: string, actorId: string) => Promise<{
    success: boolean;
    error?: string;
    poolSnapshotId?: string;
    criteriaSnapshotId?: string;
  }>;
  
  /**
   * Check if a session's configuration is locked.
   */
  isConfigurationLocked: (sessionId: string) => boolean;
  
  /**
   * Get lock status details for a session.
   */
  getConfigurationLockStatus: (sessionId: string) => {
    locked: boolean;
    lockedAt?: Date;
    lockedBy?: string;
    poolSnapshotId?: string;
    criteriaSnapshotId?: string;
  };
  
  // ================================
  // Participants
  // ================================
  
  addParticipant: (sessionId: string, userId: string) => void;
  
  removeParticipant: (sessionId: string, userId: string) => void;
  
  setFacilitator: (sessionId: string, userId: string) => void;
  
  // ================================
  // Criteria Configuration
  // ================================
  
  updateCriteriaConfig: (
    sessionId: string,
    config: Partial<SessionCriteriaConfig>
  ) => void;
  
  addCriterion: (sessionId: string, criterionId: string, weight?: number) => void;
  
  removeCriterion: (sessionId: string, criterionId: string) => void;
  
  updateCriterionWeight: (sessionId: string, criterionId: string, weight: number) => void;
  
  setReconciliationRequired: (sessionId: string, attributeKeys: string[]) => void;
  
  // ================================
  // Items (Session Pool)
  // ================================
  
  /** Add an item to the session pool */
  addItem: (sessionId: string, itemId: string) => Promise<SessionItemAddOutcome>;
  
  /** Remove an item from the session pool */
  removeItem: (sessionId: string, itemId: string) => Promise<void>;
  
  /**
   * Add several items to the session pool, one after another (concurrent adds
   * race each other for the same item_ids column), and report which were
   * saved and which the session refused — so a bulk caller never says
   * "14 added" when the session was locked after the third.
   */
  addItems: (sessionId: string, itemIds: string[]) => Promise<{ added: string[]; failed: Array<{ itemId: string; reason: string }> }>;
  
  /**
   * Replace the session pool with the given item IDs (one policy check).
   * Use when syncing from track pool so items selected in the track auto-appear in the session.
   */
  setSessionItemIds: (sessionId: string, itemIds: string[]) => void;
  
  computeItemEligibility: (sessionId: string, itemId: string) => ItemEligibility;
  
  recomputeAllEligibility: (sessionId: string) => void;

  /** Replace the isolated items list (free-form labels for isolated sessions). */
  setIsolatedItems: (sessionId: string, items: string[]) => void;


  // ================================
  // Tool Configuration
  // ================================
  
  /**
   * Set the selected tool for a session.
   * Per Window 4: Validates tool compatibility before allowing selection.
   * Returns result with success/failure and any blockers or warnings.
   */
  setSelectedTool: (sessionId: string, toolId: string, options?: { participantCount?: number }) => { 
    success: boolean; 
    error?: string; 
    blockers?: string[]; 
    warnings?: string[];
  };
  
  updateToolConfig: (sessionId: string, config: Record<string, unknown>) => void;
  
  // ================================
  // Results
  // ================================
  
  setRankingOutput: (sessionId: string, output: Omit<SessionRankingOutput, 'overrides' | 'lastModifiedAt'>) => void;
  
  // addRankOverride retired (migration 768): a rank override is the governed
  // override_session_rank RPC (overrideSessionRank in lib/governedTransactions),
  // not a client-only append — reason, Decision and group notice server-side.

  /**
   * Governed cut-line move: state gate (RUN_COMPLETE only), `lists.move_cutline`
   * policy, durable write, decision record. Resolves with the refusal reason.
   */
  /** Governed (set_session_cut_line, mig 749): facilitator or workspace admin, RUN_COMPLETE only; recorded as a Decision.
   *  `rationaleParts` (985) lands on that Decision's rationale columns. */
  setCutLine: (
    sessionId: string,
    position: number,
    rationale?: string,
    rationaleParts?: RationaleParts | null,
  ) => Promise<{ success: boolean; error?: string }>;
  
  setResultingItemPool: (sessionId: string, itemPoolId: string) => void;
  
  // ================================
  // Decisions
  // ================================
  
  recordDecision: (sessionId: string, decision: Omit<SessionDecision, 'id' | 'madeAt'>) => void;
  
  // ================================
  // Queries
  // ================================
  
  getSessionsForCycle: (cycleId: string) => PrioritizationSession[];
  
  getStandaloneSessions: () => PrioritizationSession[];
  
  /** Get items eligible for ranking in this session */
  getEligibleItems: (sessionId: string) => string[];
  
  /** Get items with value gaps that need resolution */
  getItemsWithGaps: (sessionId: string) => string[];
  
  /** Get items that are not eligible for ranking */
  getIneligibleItems: (sessionId: string) => string[];
  
  // ================================
  // Scale Readiness Enforcement
  // ================================
  
  /**
   * Check if a session with the given intent can be created for a cycle.
   * 
   * Per scale readiness enforcement policy (2026-01-22):
   * - 'converge_values', 'rank_priorities', 'select_item_pool' require all criteria scales to be defined
   * - 'select_attributes', 'calibration', 'review' are NOT blocked by scale readiness
   * 
   * @returns { allowed: boolean, blockers: string[] }
   */
  canCreateSessionWithIntent: (params: {
    cycleId: string;
    intent: SessionIntent;
    criteriaScalesReady: boolean;
    missingScales: string[];
    criteriaLocked: boolean;
  }) => { allowed: boolean; blockers: string[] };

  // ================================
  // Real-Time Collaboration (Brief F)
  // ================================

  /** Active Realtime channels keyed by session_id */
  _sessionChannels: Record<string, RealtimeChannel>;

  /** Connection status per session_id: 'connected' | 'reconnecting' | 'disconnected' */
  _sessionChannelStatus: Record<string, 'connected' | 'reconnecting' | 'disconnected'>;

  /** Sessions where the last writeSessionEvent DB insert failed (sync may be paused for others). */
  _syncPausedSessions: Set<string>;

  /** Subscribe to Supabase Realtime for a session. Idempotent — safe to call multiple times. */
  subscribeToSessionRealtime: (sessionId: string) => Promise<void>;

  /** Unsubscribe and remove the Realtime channel for a session. Call on component unmount. */
  unsubscribeFromSessionRealtime: (sessionId: string) => void;

  /** Apply an incoming session_events row to local store state. Must be idempotent. */
  _applySessionEvent: (event: SessionRealtimeEvent) => void;

  /** Write a session_event row to Supabase. Non-blocking — failures are logged but never throw.
   *  Facilitator-only event types are rejected with an error if actorId ≠ session.facilitatorId. */
  writeSessionEvent: (params: {
    sessionId: string;
    workspaceId: string;
    actorId: string;
    actorName: string;
    eventType: string;
    payload?: Record<string, unknown>;
  }) => Promise<{ error?: string }>;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * A new session's criteria weights, as shares of 1 (criteriaConfig.weights): the
 * track's weights when it gives one for every criterion (migration 1047), else an
 * even split. The rule a track's weights follow is criteriaFromTrackAttributeKeys
 * (src/lib/sessionCriteriaFromTrack.ts); the caller passes what it gives.
 */
export function initialSessionWeights(ids: readonly string[], trackWeights?: Record<string, number>): Record<string, number> {
  const given = ids.map((id) => trackWeights?.[id]);
  const usable = given.every((w): w is number => typeof w === 'number' && Number.isFinite(w) && w >= 0);
  const sum = usable ? (given as number[]).reduce((s, w) => s + w, 0) : 0;
  if (usable && sum > 0) return Object.fromEntries(ids.map((id, i) => [id, (given[i] as number) / sum]));
  return Object.fromEntries(ids.map((id) => [id, 1 / Math.max(1, ids.length)]));
}

function generateId(): string {
  return generateUUID();
}

function computeEligibilityStatus(
  missingAttributes: string[],
  missingAcceptedValues: string[],
  hasExceptions: boolean
): ItemEligibilityStatus {
  if (missingAttributes.length > 0) {
    return 'INELIGIBLE_STRUCTURAL';
  }
  if (missingAcceptedValues.length > 0 && !hasExceptions) {
    return 'ELIGIBLE_WITH_GAPS';
  }
  return 'ELIGIBLE';
}

async function resolveExecutionLineageId(params: {
  cycleId?: string;
  trackId?: string | null;
  egapIterationId?: string;
  actorId: string;
}): Promise<string | undefined> {
  if (params.egapIterationId) {
    return params.egapIterationId;
  }

  if (!params.cycleId || !params.trackId) {
    return undefined;
  }

  const { useCycleStore } = await import('./degapProcessStore');
  return await useCycleStore.getState().getOrCreateActiveIteration(params.cycleId, params.actorId);
}

// ============================================================================
// Lock Enforcement Helpers
// ============================================================================

/**
 * Check if a session configuration modification is allowed.
 * Per Canon: "Configuration locks at session start. Post-lock changes require
 * a new session instance."
 */
function isConfigModificationAllowed(session: PrioritizationSession): { allowed: boolean; reason?: string } {
  // E03: PUBLISHED is a terminal, immutable state. Canon §E03.
  if (session.state === 'PUBLISHED') {
    return {
      allowed: false,
      reason: 'Session is published. Published results cannot be mutated.',
    };
  }
  if (session.configurationLockedAt) {
    return {
      allowed: false,
      reason: 'Configuration is locked. Post-lock changes require a new session instance.',
    };
  }
  return { allowed: true };
}

// ============================================================================
// SIMP (Session Item Mutation Policy) helpers
// Canon reference: DOCS_V4/00-canon/09A-session-edge-case-rules.md §2.2
// ============================================================================

/**
 * The workspace's rule for item changes after a vote (canon 11 §2.2). The
 * database applies it to every change, from any screen (migration 1063); this
 * copy only lets a screen refuse early under lock instead of after the save.
 */
function getWorkspaceSimpPolicy(): ItemChangeRule {
  try {
    return itemChangeRule(useTenantStore.getState().getCurrentWorkspace?.()?.sessionItemMutationPolicy);
  } catch {
    return 'lock';
  }
}

/**
 * Count tool_session_responses for all tool_sessions linked to this
 * prioritization_session. Returns 0 when Supabase is unavailable (fail-open).
 */
async function countSessionResponses(sessionId: string): Promise<number> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return 0;

  const { data: toolSessions } = await supabase
    .from('tool_sessions')
    .select('id')
    .eq('session_id', sessionId);

  if (!toolSessions?.length) return 0;

  const toolSessionIds = (toolSessions as { id: string }[]).map((ts) => ts.id);
  const { count } = await supabase
    .from('tool_session_responses')
    .select('id', { count: 'exact', head: true })
    .in('tool_session_id', toolSessionIds);

  return count ?? 0;
}

/**
 * Before an item-set change: 'block' when people have voted and the rule is
 * lock, so the screen says so before saving; otherwise 'proceed' — the
 * database clears, keeps or notes the votes and tells the voters (1063).
 */
async function evaluateSimpForMutation(sessionId: string): Promise<'block' | 'proceed'> {
  if (getWorkspaceSimpPolicy() !== 'lock') return 'proceed';
  return (await countSessionResponses(sessionId)) > 0 ? 'block' : 'proceed';
}

export const ITEMS_LOCKED_AFTER_VOTES =
  'People have already voted in this session, so its items are locked. To change them, start a new session; a workspace admin can change this rule on Settings → Workflows.';

// ============================================================================
// Session Store
// ============================================================================

export const useSessionStore = create<SessionStoreState>()(
  (set, get) => ({
    sessions: [],
    retiredSessions: [],
    activeSessionId: null,
    lastCreateError: null,
    lastCreateWarning: null,
    syncState: createInitialSyncState(),
    _sessionChannels: {},
    _sessionChannelStatus: {},
    _syncPausedSessions: new Set<string>(),
      
      // ================================
      // Supabase Sync Actions
      // ================================
      
      fetchFromSupabase: async () => {
        if (!isSupabaseAvailable()) {
          console.log('[SessionStore] Supabase not available');
          return;
        }
        
        set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });
        
        const result = await fetchAll<PrioritizationSession>(TABLE_NAME, { column: 'created_at', ascending: false }, { filterByWorkspace: true, strictWorkspaceFilter: true });
        
        if (result.error) {
          set({ 
            syncState: { 
              ...get().syncState, 
              isLoading: false, 
              syncError: result.error 
            } 
          });
          console.error('[SessionStore] Failed to fetch from Supabase:', result.error);
          return;
        }
        
        if (result.data) {
          const fromDb = (result.data as unknown as Record<string, unknown>[]).map(
            row => fromDbFormat<PrioritizationSession>(row)
          );
          const sessionIds = fromDb.map(s => s.id);
          // Session direct users: session_participants table is canonical when present; session row participant_ids is backward-compat fallback.
          let participantIdsBySession: Record<string, string[]> = {};
          if (sessionIds.length > 0) {
            const supabase = getSafeSupabaseClient();
            if (supabase) {
              try {
                const { data: spRows } = await supabase
                  .from('session_participants')
                  .select('session_id, user_id')
                  .in('session_id', sessionIds)
                  .eq('status', 'active');
                for (const sid of sessionIds) participantIdsBySession[sid] = [];
                if (spRows?.length) {
                  for (const row of spRows) {
                    const sid = row.session_id as string;
                    const uid = row.user_id as string;
                    if (!participantIdsBySession[sid].includes(uid)) participantIdsBySession[sid].push(uid);
                  }
                }
              } catch {
                // session_participants table may not exist; keep participant_ids from session row (leave participantIdsBySession empty)
                participantIdsBySession = {};
              }
            }
          }
          const fromDbWithCanonicalParticipants = fromDb.map((s) => {
            if (s.id in participantIdsBySession) return { ...s, participantIds: participantIdsBySession[s.id] };
            return s;
          });
          const existingSessions = get().sessions;
          // Stage 7 Slice 6: Do not resurrect local-only sessions — only DB-backed data (no false reality).
          const mergedFromDb = fromDbWithCanonicalParticipants.map((dbSession) => {
            const local = existingSessions.find(s => s.id === dbSession.id);
            if (!local) return dbSession;
            return mergeSessionFromDb(local, dbSession);
          });
          // Sessions = only what came from DB (merged with local wins on updatedAt for those in DB)
          const mergedSessions = mergedFromDb as PrioritizationSession[];
          // Stage 2A: infer sessionContextMode when missing (DB has no column until 2B); existing sessions = cycle_linked
          const withInferredMode = mergedSessions.map((s) => ({
            ...s,
            itemIds: s.itemIds ?? [],
            isolatedItems: s.isolatedItems ?? [],
            participantIds: s.participantIds ?? [],
            itemEligibility: s.itemEligibility ?? {},
            criteriaConfig: s.criteriaConfig ?? { criteriaIds: [], weights: {}, reconciliationRequiredSet: [] },
            sessionContextMode: s.sessionContextMode ?? (s.trackId != null && s.trackId !== '' ? 'cycle_linked' : 'app_linked'),
          }));

          set({
            sessions: withInferredMode.filter((s) => !isSessionRetired(s)),
            retiredSessions: withInferredMode.filter(isSessionRetired),
            syncState: {
              ...get().syncState,
              isLoading: false,
              lastSyncAt: new Date(),
              syncError: null,
            },
          });
          console.log(`[SessionStore] Loaded ${fromDb.length} from Supabase (DB-only; no local-only merge). Total: ${mergedSessions.length}`);
        }
      },

      fetchSessionById: async (sessionId: string) => {
        if (!isSupabaseAvailable()) return false;
        const result = await fetchById<PrioritizationSession>(TABLE_NAME, sessionId);
        if (result.error || !result.data) {
          console.warn('[SessionStore] fetchSessionById failed:', result.error ?? 'no data');
          return false;
        }
        let session = result.data as PrioritizationSession;
        const supabase = getSafeSupabaseClient();
        if (supabase) {
          try {
            const { data: spRows } = await supabase
              .from('session_participants')
              .select('session_id, user_id')
              .eq('session_id', sessionId)
              .eq('status', 'active');
            if (spRows?.length) {
              const participantIds = spRows.map((r: { user_id: string }) => r.user_id);
              session = { ...session, participantIds };
            }
          } catch {
            // session_participants may not exist
          }
        }
        const withDefaults: PrioritizationSession = {
          ...session,
          itemIds: session.itemIds ?? [],
          isolatedItems: session.isolatedItems ?? [],
          participantIds: session.participantIds ?? [],
          itemEligibility: session.itemEligibility ?? {},
          criteriaConfig: session.criteriaConfig ?? { criteriaIds: [], weights: {}, reconciliationRequiredSet: [] },
          sessionContextMode: session.sessionContextMode ?? (session.trackId != null && session.trackId !== '' ? 'cycle_linked' : 'app_linked'),
        };
        set((state) => {
          // A retired session lives apart so no list shows it (mig 758).
          if (isSessionRetired(withDefaults)) {
            return {
              sessions: state.sessions.filter((s) => s.id !== sessionId),
              retiredSessions: [...state.retiredSessions.filter((s) => s.id !== sessionId), withDefaults],
            };
          }
          const existing = state.sessions.find((s) => s.id === sessionId);
          const merged = existing ? mergeSessionFromDb(existing, withDefaults) : withDefaults;
          const sessions = existing
            ? state.sessions.map((s) => (s.id === sessionId ? merged : s))
            : [...state.sessions, merged];
          return { sessions, retiredSessions: state.retiredSessions.filter((s) => s.id !== sessionId) };
        });
        // Brief F: subscribe to realtime after session is loaded into store
        void get().subscribeToSessionRealtime(sessionId);
        return true;
      },

      // ================================
      // Real-Time Collaboration (Brief F)
      // ================================

      subscribeToSessionRealtime: async (sessionId: string) => {
        const supabase = getSafeSupabaseClient();
        if (!supabase || get()._sessionChannels[sessionId]) return; // idempotent guard

        // Full re-fetch — used on initial load AND on I01 reconnect
        const fetchCurrentState = async () => {
          await get().fetchSessionById(sessionId);
        };

        const channel = supabase
          .channel(`session-realtime:${sessionId}`)
          // 1. Broadcast session row updates (state machine transitions)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'prioritization_sessions',
              filter: `id=eq.${sessionId}`,
            },
            (payload) => {
              if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
                const updated = fromDbFormat<PrioritizationSession>(payload.new as Record<string, unknown>);
                set((state) => {
                  const existing = state.sessions.find((s) => s.id === sessionId);
                  const sessions = existing
                    ? state.sessions.map((s) => (s.id === sessionId ? { ...existing, ...updated } : s))
                    : [...state.sessions, updated];
                  return { sessions };
                });
              }
            }
          )
          // 2. Broadcast session_events (durable action log)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'session_events',
              filter: `session_id=eq.${sessionId}`,
            },
            (payload) => {
              get()._applySessionEvent(payload.new as SessionRealtimeEvent);
            }
          )
          // 3. I01 reconnect — re-fetch full state to reconcile missed events
          .on('system', {}, (payload) => {
            if ((payload as { status?: string }).status === 'SUBSCRIBED') {
              void fetchCurrentState();
            }
          })
          .subscribe((status) => {
            if (status === 'SUBSCRIBED') {
              set((s) => ({
                _sessionChannelStatus: { ...s._sessionChannelStatus, [sessionId]: 'connected' },
              }));
            } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
              set((s) => ({
                _sessionChannelStatus: { ...s._sessionChannelStatus, [sessionId]: 'reconnecting' },
              }));
            } else if (status === 'CLOSED') {
              set((s) => ({
                _sessionChannelStatus: { ...s._sessionChannelStatus, [sessionId]: 'disconnected' },
              }));
            }
          });

        set((s) => ({ _sessionChannels: { ...s._sessionChannels, [sessionId]: channel } }));
      },

      unsubscribeFromSessionRealtime: (sessionId: string) => {
        const supabase = getSafeSupabaseClient();
        const channel = get()._sessionChannels[sessionId];
        if (channel && supabase) void supabase.removeChannel(channel);
        set((s) => {
          const channels = { ...s._sessionChannels };
          delete channels[sessionId];
          const statuses = { ...s._sessionChannelStatus };
          delete statuses[sessionId];
          const paused = new Set(s._syncPausedSessions);
          paused.delete(sessionId);
          return { _sessionChannels: channels, _sessionChannelStatus: statuses, _syncPausedSessions: paused };
        });
      },

      _applySessionEvent: (event: SessionRealtimeEvent) => {
        switch (event.event_type) {
          case 'item_scored': {
            // Scores are held in tool_session_responses; local session store holds rankingOutput.
            // A full re-fetch reconciles any score drift — handled on reconnect.
            // No-op here: tool session realtime channel handles score broadcasts.
            break;
          }
          case 'item_moved': {
            const { item_id, to_rank } = event.payload as { item_id?: string; to_rank?: number };
            if (!item_id || to_rank === undefined) break;
            set((s) => ({
              sessions: s.sessions.map((sess) => {
                if (sess.id !== event.session_id) return sess;
                const ids = [...(sess.itemIds ?? [])];
                const fromIdx = ids.indexOf(item_id);
                if (fromIdx === -1) return sess;
                ids.splice(fromIdx, 1);
                ids.splice(Math.max(0, to_rank - 1), 0, item_id);
                return { ...sess, itemIds: ids };
              }),
            }));
            break;
          }
          case 'item_added': {
            const { item_id } = event.payload as { item_id?: string };
            if (!item_id) break;
            set((s) => ({
              sessions: s.sessions.map((sess) => {
                if (sess.id !== event.session_id) return sess;
                if ((sess.itemIds ?? []).includes(item_id)) return sess; // idempotent
                return { ...sess, itemIds: [...(sess.itemIds ?? []), item_id] };
              }),
            }));
            break;
          }
          case 'item_removed': {
            const { item_id } = event.payload as { item_id?: string };
            if (!item_id) break;
            set((s) => ({
              sessions: s.sessions.map((sess) => {
                if (sess.id !== event.session_id) return sess;
                return { ...sess, itemIds: (sess.itemIds ?? []).filter((id) => id !== item_id) };
              }),
            }));
            break;
          }
          case 'session_phase_advanced': {
            // Phase is not a direct field on PrioritizationSession (uses `state`); re-fetch to reconcile.
            void get().fetchSessionById(event.session_id);
            break;
          }
          case 'result_locked': {
            // Facilitator locked results; if session is not already RUN_COMPLETE, re-fetch.
            const sess = get().sessions.find((s) => s.id === event.session_id);
            if (sess && sess.state !== 'RUN_COMPLETE' && sess.state !== 'PUBLISHED') {
              void get().fetchSessionById(event.session_id);
            }
            break;
          }
          case 'participant_joined': {
            // Supplement to presenceStore — update participantIds optimistically
            const { user_id } = event.payload as { user_id?: string };
            if (!user_id) break;
            set((s) => ({
              sessions: s.sessions.map((sess) => {
                if (sess.id !== event.session_id) return sess;
                if ((sess.participantIds ?? []).includes(user_id)) return sess; // idempotent
                return { ...sess, participantIds: [...(sess.participantIds ?? []), user_id] };
              }),
            }));
            break;
          }
          default:
            console.warn('[SessionStore] Unknown session_event type:', event.event_type);
        }
      },

      writeSessionEvent: async (params) => {
        // Facilitator guard — Brief F §2: only the session facilitator may write these event types.
        if (FACILITATOR_ONLY_EVENTS.has(params.eventType)) {
          const session = get().sessions.find((s) => s.id === params.sessionId);
          if (!session) {
            const msg = `[SessionStore] writeSessionEvent: session ${params.sessionId} not found — cannot verify facilitator identity`;
            console.error(msg);
            return { error: 'Session not found — cannot verify facilitator identity' };
          }
          if (session.facilitatorId && params.actorId !== session.facilitatorId) {
            const errMsg = `403: Only the session facilitator may write '${params.eventType}' events`;
            console.error(`[SessionStore] writeSessionEvent: ${errMsg}`);
            // Dispatch DOM event so drag/move UIs can surface the named reason and revert.
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('session-facilitator-guard', {
                detail: {
                  sessionId: params.sessionId,
                  eventType: params.eventType,
                  message: 'Only the session facilitator can move items during a live session.',
                },
              }));
            }
            return { error: errMsg };
          }
        }

        const supabase = getSafeSupabaseClient();
        if (!supabase) return {};

        const { error } = await supabase.from('session_events').insert({
          session_id: params.sessionId,
          workspace_id: params.workspaceId,
          actor_id: params.actorId,
          actor_name: params.actorName,
          event_type: params.eventType,
          payload: params.payload ?? {},
        });

        if (error) {
          // Log but do not block the originating action — events are supplementary.
          // Mark sync as paused so the UI can surface a warning to the actor.
          console.warn('[SessionStore] Failed to write session_event:', error);
          set((s) => {
            const paused = new Set(s._syncPausedSessions);
            paused.add(params.sessionId);
            return { _syncPausedSessions: paused };
          });
          return { error: error.message };
        }

        // Successful write — clear any paused state for this session.
        set((s) => {
          if (!s._syncPausedSessions.has(params.sessionId)) return s;
          const paused = new Set(s._syncPausedSessions);
          paused.delete(params.sessionId);
          return { _syncPausedSessions: paused };
        });

        return {};
      },

      syncToSupabase: async (session: PrioritizationSession, options) => {
        // Stage 7: Do not report success when Supabase is unavailable (Build Contract).
        if (!isSupabaseAvailable()) {
          return { success: false, error: 'Supabase not available' };
        }
        
        set({ syncState: { ...get().syncState, isSyncing: true } });
        
        // Use upsert to handle both insert and update cases
        // This is important because sessions may only exist in memory if earlier inserts failed
        const supabase = getSafeSupabaseClient();
        if (!supabase) {
          set({ syncState: { ...get().syncState, isSyncing: false } });
          return { success: false, error: 'Supabase not available' };
        }
        
        try {
          const dbRecord = toDbFormat(session as unknown as Record<string, unknown>);
          // Omit columns not in DB schema (prioritization_sessions has no session_type)
          delete (dbRecord as Record<string, unknown>)['session_type'];
          // deliberation_record_id is a TS-only enrichment (the relationship lives on
          // deliberation_records.session_id) — same omit as insertRecord/updateRecord.
          delete (dbRecord as Record<string, unknown>)['deliberation_record_id'];
          // This is a whole-row save from memory, and what is in memory can be behind
          // the server. run_session owns the run's outcome; saving a copy from before
          // it put a finished session back to DRAFT, and the run was lost.
          if (!options?.includeState) {
            delete (dbRecord as Record<string, unknown>)['state'];
            delete (dbRecord as Record<string, unknown>)['run_completed_at'];
            delete (dbRecord as Record<string, unknown>)['ranking_output'];
          }

          const { error } = await supabase
            .from(TABLE_NAME)
            .upsert(dbRecord, { onConflict: 'id' })
            .select()
            .single();
          
          set({ syncState: { ...get().syncState, isSyncing: false } });
          
          if (error) {
            console.error('[SessionStore] Failed to sync to Supabase:', error);
            return { success: false, error: error.message };
          }
          
          console.log('[SessionStore] Session synced to Supabase:', session.id, 'state:', session.state);
          return { success: true };
        } catch (err) {
          set({ syncState: { ...get().syncState, isSyncing: false } });
          const message = err instanceof Error ? err.message : 'Unknown error';
          console.error('[SessionStore] Exception syncing to Supabase:', err);
          return { success: false, error: message };
        }
      },
      
      // ================================
      // Session CRUD
      // ================================
      
      createSession: async (params) => {
        // Reset the surfaced failure reason for this attempt. Failure paths below
        // that return undefined set this so callers can show the real cause.
        set({ lastCreateError: null, lastCreateWarning: null });
        // Collected below by warn-and-proceed gates that let creation continue
        // but still owe the user a reason. Joined into lastCreateWarning at the
        // end of the gate chain so a caller sees every note, not just the last.
        const createWarnings: string[] = [];
        // =========================================================================
        // SCALE READINESS ENFORCEMENT (per user decision 2026-01-22):
        // =========================================================================
        // Scale readiness is a HARD GATE for execution intents:
        // - 'converge_values': Requires all criteria scales defined
        // - 'rank_priorities': Requires all criteria scales defined
        // - 'select_item_pool': Requires all criteria scales defined
        //
        // NOT blocked by scale readiness (early Catchball work):
        // - 'select_attributes': Criteria selection happens before scales
        // - 'calibration': Review/calibration sessions
        // - 'review': Review sessions
        //
        // IMPORTANT: The UI (GatherExecutionSection) is the primary enforcement layer.
        // Sessions with execution intents should only be created after passing UI gates.
        // This comment documents the contract; UI validation must occur before reaching here.
        // =========================================================================

        const canonicalDecisionClass = INTENT_TO_DECISION_CLASS[params.intent] ?? 'item_selection';

        // =========================================================================
        // STAGE × MECHANISM VALIDATION (Canon 23 §7.2, Gap 3):
        // =========================================================================
        // When a selectedToolId is provided at creation time, validate the
        // stage × mechanism combination before doing anything else. A not_valid
        // combination is a hard block — the session must not be created.
        // Warnings are logged to console but do not block creation.
        // =========================================================================
        if (params.selectedToolId) {
          const mechanismTags = getMechanismTagsForToolId(params.selectedToolId);
          const primaryMechanism = mechanismTags[0];
          if (primaryMechanism) {
            const canonicalStage = getCanonicalSessionTypeForIntent(params.intent);
            const verdict = validateStageMechanismFit(canonicalStage, primaryMechanism);
            if (verdict.validity === 'not_valid') {
              const reason = verdict.notes || `Tool "${params.selectedToolId}" is not valid at stage "${canonicalStage}"`;
              console.warn('[SessionStore] createSession blocked by stage×mechanism validator:', reason);
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('session-blocked', {
                  detail: { store: 'session', action: 'createSession', reason: 'stage_mechanism_invalid', message: reason },
                }));
              }
              throw new Error(reason);
            }
            if (verdict.validity === 'warning' && verdict.notes) {
              console.warn('[SessionStore] createSession stage×mechanism advisory:', verdict.notes);
            }
          }
        }

        // Decision loop gating - LOG WARNING but don't block session creation.
        // Per build contract: UI is the primary enforcement layer.
        // Track-authoritative model allows sessions to be created before all loops complete.
        if (canonicalDecisionClass === 'item_prioritization' && params.cycleId) {
          try {
            const loops = await useDecisionLoopStore.getState().getLoopsForCycle(params.cycleId);
            const incompleteClassIds: string[] = [];
            for (const classId of LOOPS_REQUIRED_BEFORE_ITEM_PRIORITIZATION) {
              const loop = loops.find((l) => l.decisionClassId === classId);
              if (!loop || loop.status !== 'COMPLETE') {
                console.warn(`[SessionStore] Decision loop ${classId} is not complete - proceeding anyway (UI should enforce gates)`);
                incompleteClassIds.push(classId);
              }
            }
            if (incompleteClassIds.length > 0) {
              createWarnings.push(`This cycle still has an incomplete decision loop (${incompleteClassIds.join(', ')}) — the session was created anyway.`);
            }
          } catch (loopCheckError) {
            console.warn('[SessionStore] Could not check decision loops:', loopCheckError);
          }
        }

        // =========================================================================
        // TRACK BOUNDARY VALIDATION (v2.2 Canon):
        // =========================================================================
        // Sessions must respect Track boundaries:
        // - If session has an egapIterationId, the iteration's loop must have itemPoolId (Track)
        // - For force-ranking (item_prioritization) sessions, Track is required
        // - Items in the session must be in the Track's pool
        // =========================================================================
        if (params.egapIterationId && canonicalDecisionClass === 'item_prioritization') {
          const { useExecutionLineageStore } = await import('./egapIterationStore');
          const iteration = useExecutionLineageStore.getState().getIteration(params.egapIterationId);
          
          if (iteration?.catchballLoopId) {
            const { useCatchballLoopStore } = await import('./catchballLoopStore');
            const loop = useCatchballLoopStore.getState().getLoop(iteration.catchballLoopId);
            
            if (!loop?.itemPoolId) {
              console.warn('[SessionStore] Force-ranking session requires loop to have itemPoolId (Track). Loop:', iteration.catchballLoopId);
            }
          }
        }

        if (params.intent === 'rank_priorities' && params.trackId) {
          const initialItemIds = params.initialItemIds ?? [];
          const trackItemIds = initialItemIds.length > 0
            ? initialItemIds
            : await (async () => {
                const { useItemPoolsStore } = await import('./itemPoolsStore');
                const itemPoolsState = useItemPoolsStore.getState();
                if (itemPoolsState.itemPools.length === 0 && isSupabaseAvailable()) {
                  await itemPoolsState.fetchFromSupabase();
                }
                const trackPool = useItemPoolsStore.getState().itemPools.find((pool) => pool.trackId === params.trackId);
                return trackPool?.priorityIds ?? [];
              })();
          const relationshipState = await import('./itemRelationshipsStore');
          const relationshipStore = relationshipState.useItemRelationshipsStore.getState();
          if (
            relationshipStore.groups.length === 0 &&
            relationshipStore.groupMemberships.length === 0 &&
            relationshipStore.edges.length === 0 &&
            isSupabaseAvailable()
          ) {
            await relationshipStore.fetchFromSupabase();
          }
          const relationshipReadiness = summarizeTrackRelationshipReadiness(
            trackItemIds.map((id) => ({ id })),
            relationshipState.useItemRelationshipsStore.getState().groups,
            relationshipState.useItemRelationshipsStore.getState().groupMemberships,
            relationshipState.useItemRelationshipsStore.getState().edges,
          );
          const relationshipBlockerMessage = getTrackRelationshipBlockerMessage(relationshipReadiness);
          if (relationshipBlockerMessage) {
            // Relationship review is a soft warning, not a hard blocker — the user
            // has already been informed in the UI. Log and proceed.
            console.warn('[SessionStore] createSession: proceeding with unreviewed relationships:', relationshipBlockerMessage);
            createWarnings.push(relationshipBlockerMessage);
          }

          // ─────────────────────────────────────────────────────────────────────
          // Anti-pattern HARD GATE (GRAPH_POOL_VIOLATIONS)
          // A ranking session over a pool with block-disposition anti-patterns
          // (unresolved duplicate cluster) cannot start until resolved. A partial
          // package is NOT among them (owner ruling 2026-10-12: flagged, never
          // disallowed). Comparing unlike things is NOT among them: per the owner
          // ruling of 2026-09-04 (canon 02 §8), a Comparison Cell mismatch is
          // surfaced as a warning and never stops a session happening or
          // proceeding — the room is told, and the room decides.
          // See src/lib/poolViolations.ts and demo/PRIORITIZATION_ANTIPATTERNS.md.
          // ─────────────────────────────────────────────────────────────────────
          if (isFeatureEnabled(FEATURE_FLAGS.GRAPH_POOL_VIOLATIONS) && trackItemIds.length > 0) {
            const relState = relationshipState.useItemRelationshipsStore.getState();
            const report = computeViolations(trackItemIds, {
              items: useItemsStore.getState().items,
              edges: relState.edges,
              groups: relState.groups,
              memberships: relState.groupMemberships,
              lineageEvents: relState.lineageEvents ?? [],
            });
            const blocks = report.violations.filter((v) => v.disposition === 'block');
            if (blocks.length > 0) {
              const names = blocks.map((b) => b.name).join('; ');
              const userMessage = `Cannot start ranking — this pool isn't comparable yet: ${names}. Resolve these before ranking.`;
              console.warn('[SessionStore] createSession blocked by pool violations:', blocks);
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('session-blocked', {
                  detail: { store: 'session', action: 'createSession', reason: 'pool_violations', message: userMessage, violations: blocks },
                }));
              }
              throw new Error(userMessage);
            }
          }
        }

        const now = new Date();

        // A session on a cycle or track starts from the chain's defaults (S-40,
        // get_session_defaults, migration 1039): the run mode when the caller
        // names none, and — while the chain does not let sessions override —
        // the run mode and default facilitator whatever was asked. The server
        // applies the same rule on insert; reading it here keeps this copy equal.
        const chosenMode = params.modeConfig?.runMode ?? params.modeConfig?.timingMode ?? params.modeConfig?.entryMode;
        let inherited: SessionDefaults | null = null;
        if (params.cycleId || params.trackId) {
          inherited = (await fetchSessionDefaults({ cycleId: params.cycleId, trackId: params.trackId })).defaults;
        }
        const heldByChain = inherited?.allowSessionOverride.value === false;
        const inheritedMode = inherited && (heldByChain || chosenMode === undefined)
          ? { runMode: inherited.runMode.value, timingMode: inherited.timingMode.value, entryMode: inherited.entryMode.value }
          : null;
        const facilitatorId = heldByChain && inherited?.defaultFacilitatorId.value
          ? inherited.defaultFacilitatorId.value
          : params.facilitatorId;

        // Default mode configuration
        const legacyTiming = inheritedMode?.timingMode ?? params.modeConfig?.timingMode ?? 'live';
        const legacyEntry  = inheritedMode?.entryMode ?? params.modeConfig?.entryMode  ?? 'facilitator';
        const defaultModeConfig: SessionModeConfig = {
          timingMode: legacyTiming,
          entryMode: legacyEntry,
          allowViewOtherResponses: false,
          allowResponseChanges: true,
          requireAllResponses: false,
          ...params.modeConfig,
          ...(inheritedMode && { timingMode: inheritedMode.timingMode, entryMode: inheritedMode.entryMode }),
          // D02: always stamp the canonical run mode — prefer explicit param, then derive from legacy pair.
          runMode: inheritedMode?.runMode ?? params.modeConfig?.runMode ?? deriveRunMode(legacyTiming, legacyEntry),
        };

        const mode: SessionContextMode = params.sessionContextMode ?? (params.trackId != null && params.trackId !== '' ? 'cycle_linked' : 'app_linked');
        if (mode === 'cycle_linked' && (params.trackId == null || params.trackId === '')) {
          const msg = 'This session is linked to a cycle but no track was selected. Pick a track, or create it as an independent session.';
          console.warn('[SessionStore] createSession: cycle_linked requires trackId');
          set({ lastCreateError: msg });
          return undefined;
        }
        let resolvedEgapIterationId = params.egapIterationId;
        if (mode === 'cycle_linked') {
          try {
            resolvedEgapIterationId = await resolveExecutionLineageId({
              cycleId: params.cycleId,
              trackId: params.trackId,
              egapIterationId: params.egapIterationId,
              actorId: params.createdBy,
            });
          } catch (iterationError) {
            const detail = iterationError instanceof Error ? iterationError.message : String(iterationError);
            console.error('[SessionStore] Failed to resolve cycle-execution lineage for cycle-linked session:', iterationError);
            set({ lastCreateError: `Could not link this session to the cycle's execution lineage: ${detail}` });
            return undefined;
          }
          if (!resolvedEgapIterationId) {
            console.error('[SessionStore] Could not resolve cycle-execution lineage for cycle-linked session');
            set({ lastCreateError: "Could not resolve the cycle's execution lineage for this track. Check that the track is bound to its cycle." });
            return undefined;
          }
        }
        const initialCriteriaIds = params.initialCriteriaIds ?? [];
        const initialItemIds = params.initialItemIds ?? [];
        const session: PrioritizationSession = {
          id: generateId(),
          name: params.name,
          description: params.description,
          cycleId: params.cycleId,
          egapIterationId: resolvedEgapIterationId, // Legacy field name for required cycle-execution lineage
          sessionContextMode: mode,
          trackId: params.trackId ?? null,
          catchballBallId: params.catchballBallId ?? null,
          facilitatorId,
          workspaceId: params.workspaceId ?? getCurrentWorkspaceId() ?? undefined,
          participantIds: [facilitatorId],
          criteriaConfig: {
            criteriaIds: initialCriteriaIds,
            weights: initialSessionWeights(initialCriteriaIds, params.initialCriteriaWeights),
            reconciliationRequiredSet: initialCriteriaIds,
          },
          itemIds: params.itemIds ?? initialItemIds,
          // Typed options are identified by their text, so a repeated line is the
          // same option, not a second one.
          isolatedItems: uniqueLines(params.isolatedItems ?? []),
          itemEligibility: {},
          state: 'DRAFT',
          modeConfig: defaultModeConfig,
          intent: params.intent, // Required for gate validation
          createdAt: now,
          createdBy: params.createdBy,
          updatedAt: now,
          decisions: [],
          sessionType: getLegacySessionTypeForIntent(params.intent),
          ...(params.selectedMetaCategory && { selectedMetaCategory: params.selectedMetaCategory }),
          ...(params.selectedToolId && { selectedToolId: params.selectedToolId }),
          // One key style for tool settings (src/lib/toolSettings.ts).
          ...(params.toolConfig && { toolConfig: normalizeToolConfig(params.toolConfig) }),
          // An empty vector is stored as absent so "not declared" has one spelling.
          ...(params.situationVector && !isEmptySituationVector(params.situationVector) && {
            situationVector: params.situationVector,
            situationVectorVersion: SITUATION_VECTOR_VERSION,
          }),
          ...(params.contextId && { contextId: params.contextId }),
          ...(params.originModule && { originModule: params.originModule }),
          // Stamp the active collaboration space (mirrors items pattern).
          // NULL = org-level (visible in all non-personal spaces).
          // Only stamp a space we can confirm is loaded for this user. currentSpaceId
          // is persisted to localStorage and re-validated only after fetchSpaces, so a
          // stale id (e.g. a deleted space, or one from another workspace) could be
          // present here — and space_id is an FK, so stamping it would fail the insert.
          // Fall back to org-level (null) rather than block session creation.
          spaceId: (() => {
            const spaceState = useSpaceStore.getState();
            const current = spaceState.currentSpaceId;
            if (!current) return null;
            // If spaces haven't loaded yet we cannot validate — stamp org-level (null)
            // to stay safe rather than risk an FK violation on a stale id.
            return spaceState.mySpaces.some((s) => s.id === current) ? current : null;
          })(),
        };

        // Authorization guard: session.facilitate (write-surfaces)
        const policyDecision = checkPolicyForSession('session.facilitate', session.id, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          const userMessage =
            reasonCode === 'MISSING_CAPABILITY'
              ? 'You do not have permission to create sessions. Ask your workspace admin to grant you the Facilitate Sessions permission.'
              : `Cannot create session: ${reasonCode}`;
          console.warn('[SessionStore] Policy denied for session.facilitate (createSession):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'createSession', message: userMessage, decision: policyDecision },
            }));
          }
          throw new Error(userMessage);
        }

        // Stage 7: DB insert first; state update only on success (Build Contract: no false durable reality).
        if (!isSupabaseAvailable()) {
          console.error('[SessionStore] Cannot create session: Supabase not available');
          set({ lastCreateError: 'The database connection is not available. Check your network and try again.' });
          return undefined;
        }
        const insertResult = await insertRecord<PrioritizationSession>(TABLE_NAME, session);
        if (insertResult.error) {
          console.error('[SessionStore] Failed to insert session to Supabase:', insertResult.error);
          if (session.trackId == null && insertResult.error.includes('track_id')) {
            console.warn('[SessionStore] Non-cycle session insert failed (track_id). Ensure Stage 2B migrations (174–176) are applied.');
          }
          set({ lastCreateError: `The session could not be saved: ${insertResult.error}` });
          return undefined;
        }
        // Insert succeeded: add to state so UI reflects DB truth
        set((state) => ({
          sessions: [...state.sessions, session],
        }));
        if (session.itemIds.length > 0) {
          get().recomputeAllEligibility(session.id);
        }
        // Canonical session direct users: insert facilitator into session_participants so table is source of truth from creation.
        try {
          const { useSessionParticipantsStore } = await import('./sessionParticipantsStore');
          await useSessionParticipantsStore.getState().addParticipant({
            sessionId: session.id,
            userId: facilitatorId,
            role: 'facilitator',
            addedBy: params.createdBy,
          });
        } catch (participantErr) {
          console.warn('[SessionStore] Could not add facilitator to session_participants (fallback: session.participantIds):', participantErr);
        }
        // Log audit event after session exists in DB (governance_events_has_parent constraint) — durable so audit trail is persisted (Stage 7 Slice 5).
        const sessionCreatedEvent: Omit<SessionEvent, 'id' | 'timestamp'> = {
          type: 'session_created',
          sessionId: session.id,
          sessionName: session.name,
          actorId: params.createdBy,
          actorRole: 'workspace-admin',
          ...(params.cycleId != null && {
            details: {
              cycleId: params.cycleId,
              // Gap 4: record exchange state so governance history reflects what this session was responding to.
              ...(params.exchangeState && params.exchangeState !== 'not_applicable' && { exchangeState: params.exchangeState }),
            },
          }),
        };
        const auditResult = await useAuditStore.getState().logGovernanceEventDurable(sessionCreatedEvent);
        if (!auditResult.auditSaved && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('store-error', {
            detail: { store: 'session', action: 'createSession', message: `Governance event could not be saved: ${auditResult.error ?? 'unknown'}` },
          }));
        }
        if (params.cycleId) {
          const loops = await useDecisionLoopStore.getState().getLoopsForCycle(params.cycleId);
          const loop = loops.find((l) => l.decisionClassId === canonicalDecisionClass);
          if (loop) {
            await useDecisionLoopStore.getState().markInProgress(loop.id);
          }
        }
        // Gap 2: Create DeliberationRecord for this session (Canon 23 §6.4, D03).
        // One per session, created immediately after session insert, never deleted.
        // Non-fatal — a missing record can be recreated; session creation must still succeed.
        const drClient = getSafeSupabaseClient();
        if (drClient && session.workspaceId) {
          try {
            const drId = generateUUID();
            const { error: drError } = await drClient
              .from('deliberation_records')
              .insert({
                id: drId,
                session_id: session.id,
                workspace_id: session.workspaceId,
                transcript_status: 'none',
              });
            if (drError) {
              console.warn('[SessionStore] Could not create DeliberationRecord (non-fatal):', drError.message);
            } else {
              session.deliberationRecordId = drId;
              // Patch state so the in-memory session reflects the linked record.
              set((state) => ({
                sessions: state.sessions.map((s) =>
                  s.id === session.id ? { ...s, deliberationRecordId: drId } : s
                ),
              }));
            }
          } catch (drErr) {
            console.warn('[SessionStore] DeliberationRecord creation threw (non-fatal):', drErr);
          }
        }
        if (createWarnings.length > 0) {
          set({ lastCreateWarning: createWarnings.join(' ') });
        }
        return session;
      },
      
      updateSession: async (sessionId, updates) => {
        const session = get().sessions.find(s => s.id === sessionId);
        if (!session) return { success: false, error: 'Session not found' };
        
        const policyDecision = checkPolicyForSession('session.facilitate', sessionId, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for session.facilitate (updateSession):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'updateSession', message: `Cannot update session: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return { success: false, error: reasonCode };
        }
        
        const updated = { ...session, ...updates, updatedAt: new Date() };
        
        // Stage 7 Slice 6: DB-first — persist then update state (no false reality).
        if (!isSupabaseAvailable()) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('store-warning', {
              detail: { message: 'Database not available. Session update was not saved.', type: 'error' },
            }));
          }
          return { success: false, error: 'Supabase not available' };
        }
        const result = await updateRecord<PrioritizationSession>(TABLE_NAME, sessionId, updated);
        if (result.error) {
          console.error('[SessionStore] Failed to update session in Supabase:', result.error);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('store-warning', {
              detail: { message: `Session update failed: ${result.error}`, type: 'error' },
            }));
          }
          return { success: false, error: result.error };
        }
        
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId ? updated : s
          ),
        }));
        
        if (updates.selectedGridCells !== undefined) {
          get().recomputeAllEligibility(sessionId);
        }
        return { success: true };
      },
      
      updateModeConfig: async (sessionId, modeConfigUpdates) => {
        const session = get().getSession(sessionId);
        if (!session) return { success: false, error: 'Session not found' };
        const policyDecision = checkPolicyForSession('session.facilitate', sessionId, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for session.facilitate (updateModeConfig):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'updateModeConfig', message: `Cannot update mode config: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return { success: false, error: reasonCode };
        }
        const updated = {
          ...session,
          modeConfig: { ...session.modeConfig, ...modeConfigUpdates },
          updatedAt: new Date(),
        };
        if (!isSupabaseAvailable()) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('store-warning', {
              detail: { message: 'Database not available. Mode config was not saved.', type: 'error' },
            }));
          }
          return { success: false, error: 'Supabase not available' };
        }
        const result = await updateRecord<PrioritizationSession>(TABLE_NAME, sessionId, updated);
        if (result.error) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('store-warning', {
              detail: { message: `Mode config update failed: ${result.error}`, type: 'error' },
            }));
          }
          return { success: false, error: result.error };
        }
        set((state) => ({
          sessions: state.sessions.map((s) => (s.id === sessionId ? updated : s)),
        }));
        return { success: true };
      },
      
      retireSession: async (sessionId, reason) => {
        const result = await retireSessionRpc(sessionId, reason);
        if (result.ok) {
          const now = new Date();
          set((state) => {
            const session = state.sessions.find((s) => s.id === sessionId);
            return {
              sessions: state.sessions.filter((s) => s.id !== sessionId),
              retiredSessions: session
                ? [...state.retiredSessions.filter((s) => s.id !== sessionId),
                   { ...session, retiredAt: now, retiredReason: reason.trim() }]
                : state.retiredSessions,
              activeSessionId: state.activeSessionId === sessionId ? null : state.activeSessionId,
            };
          });
        }
        return result;
      },

      restoreRetiredSession: async (sessionId, reason) => {
        const result = await restoreRetiredSessionRpc(sessionId, reason);
        if (result.ok) {
          set((state) => {
            const session = state.retiredSessions.find((s) => s.id === sessionId);
            return {
              retiredSessions: state.retiredSessions.filter((s) => s.id !== sessionId),
              sessions: session
                ? [...state.sessions.filter((s) => s.id !== sessionId),
                   { ...session, retiredAt: null, retiredBy: null, retiredReason: null }]
                : state.sessions,
            };
          });
          // Pull the authoritative row back (and resubscribe) now that it is live.
          void get().fetchSessionById(sessionId);
        }
        return result;
      },

      cloneSessionForReRun: async (originalSessionId) => {
        const original = get().getSession(originalSessionId);
        if (!original) return null;

        const supabase = getSafeSupabaseClient();
        if (!supabase) return null;

        const now = new Date();
        const newId = generateId();

        // Derive a "Re-run N" suffix by counting existing re-runs of the same original.
        const existingReRuns = get().sessions.filter(
          (s) => s.modeConfig.reRunOf === originalSessionId
        );
        const suffix = existingReRuns.length > 0 ? ` — Re-run ${existingReRuns.length + 1}` : ' — Re-run';

        const cloned: PrioritizationSession = {
          ...original,
          id: newId,
          name: `${original.name}${suffix}`,
          state: 'DRAFT',
          // Strip result fields
          rankingOutput: undefined,
          // NOTE: 'rankingDecision' was removed here — it is not (and never
          // was) a field on PrioritizationSession (the real field is
          // `decisions: SessionDecision[]`, required/non-optional). This was
          // a dead no-op assignment (nothing reads .rankingDecision); left
          // `decisions` untouched rather than guessing whether re-run clones
          // should also clear prior decisions — that's a product call.
          configurationLockedAt: undefined,
          runCompletedAt: undefined,
          publishedAt: undefined,
          publishedBy: undefined,
          resultingItemPoolId: undefined,
          deliberationRecordId: undefined,
          // Link back to original
          modeConfig: {
            ...original.modeConfig,
            reRunOf: originalSessionId,
          },
          createdAt: now,
          updatedAt: now,
          createdBy: original.facilitatorId,
        };

        const { error } = await supabase.from(TABLE_NAME).insert(cloned);
        if (error) {
          console.error('[SessionStore] cloneSessionForReRun: insert failed', error);
          return null;
        }

        set((state) => ({ sessions: [...state.sessions, cloned] }));
        return newId;
      },

      discardReRun: async (sessionId) => {
        const session = get().getSession(sessionId);
        if (!session) return { success: false, error: 'Session not found' };
        if (!session.modeConfig.reRunOf) {
          return { success: false, error: 'Not a re-run session' };
        }
        if (session.state === 'PUBLISHED') {
          return { success: false, error: 'Cannot discard a published session' };
        }
        const result = await get().retireSession(sessionId, 'Discarded re-run');
        return result.ok
          ? { success: true }
          : { success: false, error: result.remedy ? `${result.error} — ${result.remedy}` : result.error };
      },

      linkSessionToTrack: async (sessionId, trackId) => {
        const session = get().getSession(sessionId);
        if (!session) return { success: false, error: 'Session not found' };
        if (!isSupabaseAvailable()) return { success: false, error: 'Database not available' };

        const supabase = getSafeSupabaseClient();
        if (!supabase) return { success: false, error: 'Database not available' };

        const { error } = await supabase.rpc('link_session_to_track', {
          p_session_id: sessionId,
          p_track_id: trackId,
        });

        if (error) {
          console.error('[SessionStore] link_session_to_track RPC failed:', error);
          return { success: false, error: error.message };
        }

        // Optimistic update: re-fetch the session to get the updated fields
        const { data: updated } = await supabase
          .from(TABLE_NAME)
          .select('*')
          .eq('id', sessionId)
          .single();

        if (updated) {
          const mapped = fromDbFormat<PrioritizationSession>(updated as Record<string, unknown>);
          set((state) => ({
            sessions: state.sessions.map((s) => s.id === sessionId ? { ...s, ...mapped } : s),
          }));
        }

        return { success: true };
      },

      setActiveSession: (sessionId) => {
        set({ activeSessionId: sessionId });
      },
      
      getSession: (sessionId) => {
        return get().sessions.find((s) => s.id === sessionId);
      },
      
      getActiveSession: () => {
        const { activeSessionId, sessions } = get();
        return activeSessionId ? sessions.find((s) => s.id === activeSessionId) : undefined;
      },
      
      getSessionsForIteration: (egapIterationId: string) => {
        return get().sessions.filter(s => s.egapIterationId === egapIterationId);
      },
      
      getSessionsByIntent: (egapIterationId: string, intent: SessionIntent) => {
        return get().sessions.filter(
          s => s.egapIterationId === egapIterationId && s.intent === intent
        );
      },
      
      // ================================
      // Canon Hierarchy Navigation (v2.2)
      // ================================
      
      getLoopForSession: async (sessionId: string) => {
        const session = get().getSession(sessionId);
        if (!session?.egapIterationId) {
          return null;
        }
        
        // Get the cycle-execution lineage record to find its catchball loop
        const { useExecutionLineageStore } = await import('./egapIterationStore');
        const iteration = useExecutionLineageStore.getState().getIteration(session.egapIterationId);
        
        if (!iteration?.catchballLoopId) {
          return null;
        }
        
        return iteration.catchballLoopId;
      },
      
      getTrackForSession: async (sessionId: string) => {
        const loopId = await get().getLoopForSession(sessionId);
        if (!loopId) {
          return null;
        }
        
        // Get the loop to find its item_pool_id (Track)
        const { useCatchballLoopStore } = await import('./catchballLoopStore');
        const loop = useCatchballLoopStore.getState().getLoop(loopId);
        
        if (!loop?.itemPoolId) {
          return null;
        }
        
        // Get the pool to verify it's a Track and get cell coordinates
        const { useItemPoolsStore } = await import('./itemPoolsStore');
        const pool = useItemPoolsStore.getState().getPool(loop.itemPoolId);
        
        if (!pool?.isTrack || !pool.requiredItemCategory || !pool.requiredAltitudeLevel) {
          return null;
        }
        
        return {
          poolId: pool.id,
          itemCategory: pool.requiredItemCategory,
          altitudeLevel: pool.requiredAltitudeLevel,
        };
      },
      
      // ================================
      // Session State Machine
      // ================================
      
      transitionSessionState: async (sessionId, targetState, actorId, options) => {
        const session = get().getSession(sessionId);
        if (!session) {
          return { success: false, error: 'Session not found' };
        }

        const canTransition = get().canTransitionSessionState(sessionId, targetState, options);
        if (!canTransition.allowed) {
          return { success: false, error: canTransition.reason };
        }

        // Stage 6: Publish session requires session.publish on the session's cycle (configurable from Settings)
        if (targetState === 'PUBLISHED' && session.cycleId) {
          const { useCycleStore } = await import('./degapProcessStore');
          const { checkPolicyForCycle } = await import('../../lib/authorization/storeEnforcement');
          const cycle = useCycleStore.getState().getCycle(session.cycleId);
          if (cycle) {
            const decision = await checkPolicyForCycle('session.publish', session.cycleId, cycle as unknown as Record<string, unknown>);
            if (!decision.allowed) {
              const reasonCode = decision.reasons[0]?.code || 'DENIED';
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('authorization-denied', {
                  detail: { store: 'session', action: 'publish', message: `Cannot publish session: ${reasonCode}`, decision },
                }));
              }
              return { success: false, error: `Cannot publish: ${reasonCode}` };
            }
          }
        }
        
        const fromState = session.state;
        const now = new Date();
        let resolvedEgapIterationId = session.egapIterationId;
        if (targetState === 'PUBLISHED' && !resolvedEgapIterationId) {
          try {
            resolvedEgapIterationId = await resolveExecutionLineageId({
              cycleId: session.cycleId,
              trackId: session.trackId,
              egapIterationId: session.egapIterationId,
              actorId,
            });
          } catch (iterationError) {
            const message = iterationError instanceof Error ? iterationError.message : 'Failed to resolve cycle-execution lineage';
            return { success: false, error: message };
          }
          if (session.cycleId && session.trackId && !resolvedEgapIterationId) {
            return { success: false, error: 'Session must belong to the required cycle-execution lineage before publishing' };
          }
        }
        
        // Apply state-specific updates
        const stateUpdates: Partial<PrioritizationSession> = {
          state: targetState,
          updatedAt: now,
          ...(resolvedEgapIterationId ? { egapIterationId: resolvedEgapIterationId } : {}),
        };
        
        if (targetState === 'CRITERIA_FINALIZED') {
          stateUpdates.criteriaFinalizedAt = now;
          // Recompute all eligibility when criteria are finalized
          setTimeout(() => get().recomputeAllEligibility(sessionId), 0);
        } else if (targetState === 'DRAFT') {
          stateUpdates.criteriaFinalizedAt = undefined;
        } else if (targetState === 'RUN_COMPLETE') {
          if (fromState === 'DRAFT') stateUpdates.criteriaFinalizedAt = now;
          stateUpdates.runCompletedAt = now;
        } else if (targetState === 'PUBLISHED') {
          // FR-20: the artifact reached the record.
          void appTelemetry.decisionPublished('session', {
            toolId: session.selectedToolId,
            count: session.itemIds?.length ?? 0,
          });
          stateUpdates.publishedAt = now;
          stateUpdates.publishedBy = actorId;
        }
        
        const updatedSession = { ...session, ...stateUpdates };
        
        // Stage 7: Sync to DB first; update authoritative state only on success (Build Contract).
        if (!isSupabaseAvailable()) {
          return { success: false, error: 'Supabase not available' };
        }

        // §2.3a (migration 591): the state write goes through the
        // transition_session_state RPC — server-side state machine, authority
        // guard, governance event, and the authority-state trigger gate.
        // E01 optimistic concurrency is preserved via p_from_state.
        const supabaseForTransition = getSafeSupabaseClient();
        if (supabaseForTransition) {
          const { data: rpcData, error: rpcError } = await supabaseForTransition
            .rpc('transition_session_state', {
              p_session_id: sessionId,
              p_target_state: targetState,
              p_from_state: fromState,
              p_actor_id: actorId ?? null,
            });

          if (rpcError) {
            return { success: false, error: rpcError.message };
          }
          const rpcResult = rpcData as { success: boolean; error?: string } | null;
          if (!rpcResult?.success) {
            return { success: false, error: rpcResult?.error ?? 'Session state transition rejected' };
          }
        }

        // E04: reopening RUN_COMPLETE → CRITERIA_FINALIZED clears stale ranking
        // results. Canon §E04. The DELETE itself is the RPC's (771) — it runs
        // inside the same transaction as the state change, so a refused reopen
        // can no longer wipe a completed session's results.
        if (fromState === 'RUN_COMPLETE' && targetState === 'CRITERIA_FINALIZED') {
          stateUpdates.runCompletedAt = undefined;
          stateUpdates.rankingOutput = undefined;
        }

        const syncResult = await get().syncToSupabase(updatedSession, { includeState: true });
        if (!syncResult.success || syncResult.error) {
          console.error('[SessionStore] Failed to sync session state to Supabase:', syncResult.error);
          return { success: false, error: syncResult.error ?? 'Sync failed' };
        }
        // DB confirmed: now update in-memory state
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId ? updatedSession : s
          ),
        }));

        const eventWorkspaceId =
          updatedSession.workspaceId ?? getCurrentWorkspaceId() ?? null;

        // Brief F: broadcast session phase changes for scoreboard activity feed.
        if (targetState === 'CRITERIA_FINALIZED' && eventWorkspaceId) {
          void get().writeSessionEvent({
            sessionId,
            workspaceId: eventWorkspaceId,
            actorId,
            actorName: '',
            eventType: 'session_phase_advanced',
            payload: { from_state: fromState, to_state: targetState },
          });
        }

        if (targetState === 'RUN_COMPLETE') {
          const done = await applyRunCompleteSideEffects(updatedSession, actorId);
          if (!done.reportSaved) return { success: true, reportSaved: false };
        }

        // Emit session report on publish (per D-002) — awaited so we can surface failure (Stage 7 Slice 5).
        if (targetState === 'PUBLISHED') {
          const report = await useSessionReportStore.getState().createReport(updatedSession, actorId);
          if (!report && typeof window !== 'undefined') {
            const reportError = useSessionReportStore.getState().error;
            window.dispatchEvent(new CustomEvent('store-warning', {
              detail: {
                message: reportError
                  ? `Session completed but the report could not be saved: ${reportError}`
                  : 'Session state updated but report could not be saved. Completion may not be fully recorded.',
                type: 'error',
              },
            }));
            return { success: true, reportSaved: false };
          }
          if (report) {
            console.log('[SessionStore] Session report created:', report.id, 'intent:', report.intent);
          }
        }

        // Log audit event (session_state_changed is logged locally only; governance_events has event_type check)
        useAuditStore.getState().logSessionEvent({
          type: 'session_state_changed',
          sessionId,
          sessionName: session.name,
          fromState,
          toState: targetState,
          actorId,
          actorRole: 'workspace-admin',
        });
        if (targetState === 'PUBLISHED') {
          const auditResult = await useAuditStore.getState().logStage6GovernanceDurable({
            type: 'session_published',
            sessionId,
            actorId,
            actorRole: 'workspace-admin',
            details: { cycleId: session.cycleId ?? null },
          });
          if (!auditResult.auditSaved && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('store-error', {
            detail: { store: 'session', action: 'publishSession', message: `Governance event could not be saved: ${auditResult.error ?? 'unknown'}` },
          }));
            return { success: true, auditSaved: false };
          }

          // Lane bridge: mark ranking_session_published prerequisite satisfied on the active level turn
          if (
            updatedSession.intent === 'rank_priorities' ||
            updatedSession.sessionType === 'arrange'
          ) {
            import('./catchballTierTurnStore').then(async ({ useCatchballTierTurnStore }) => {
              try {
                const tierTurnStore = useCatchballTierTurnStore.getState();
                const loopId = await get().getLoopForSession(sessionId);
                if (loopId) {
                  await tierTurnStore.loadLoop(loopId, false);
                  const activePos = tierTurnStore.getActivePosition(loopId);
                  if (activePos) {
                    const loopLevelState = tierTurnStore.getLoopState(loopId);
                    const activeTurn = loopLevelState?.turns.find(t => t.id === activePos.turnId);
                    if (activeTurn) {
                      const prereq = activeTurn.judgmentPrerequisites.find(
                        p => p.code === 'ranking_session_published'
                      );
                      if (prereq && !activeTurn.prerequisiteSatisfaction[prereq.code]?.satisfied) {
                        await tierTurnStore.markPrerequisiteSatisfied({
                          turnId: activeTurn.id,
                          prereqCode: 'ranking_session_published',
                          artifactRef: { entityId: sessionId, entityTable: 'prioritization_sessions' },
                          actorId,
                        });
                      }
                    }
                  }
                }
              } catch (bridgeErr) {
                console.warn('[SessionStore] Lane bridge: could not mark prereq satisfied', bridgeErr);
              }
            }).catch(() => {});
          }

          // Participant notification is NOT sent from here any more. Migration
          // 737's trg_notify_session_published fires on the state column
          // reaching PUBLISHED, so every lane — web, iOS, an RPC, the public
          // /v1 API — notifies, and a closed tab or a dropped network no
          // longer silently swallows it. A client send here would duplicate
          // the trigger's row.

          // Voices Phase 2 (PRD §4): after results become official, append
          // counted-estimator vs. official-value rows to the calibration
          // ledger. Fire-and-forget and idempotent (unique constraint +
          // ignoreDuplicates) — never blocks or reverses a publish. No-op
          // when the session has no counted Voice responses (flag off /
          // Phase 1 sessions).
          import('../../features/voices/voiceCalibrationService')
            .then(({ recordVoiceCalibration }) => {
              recordVoiceCalibration(sessionId)
                .then(({ recorded, error }) => {
                  if (error) {
                    console.warn('[SessionStore] Voice calibration recording failed (non-fatal):', error);
                  } else if (recorded > 0) {
                    console.log(`[SessionStore] Voice calibration: ${recorded} ledger row(s) recorded`);
                  }
                })
                .catch(() => {});
            })
            .catch(() => {});

          // Auto-record decision: session published → force_ranking decision (spec §3.1)
          import('../../lib/decisionService').then(({ autoRecordDecision }) => {
            // SessionRankingOutput has no 'rankings' field — the canonical
            // ranked-items array is `rankedItemIds` (always present, never
            // optional). The old `rankings` read always evaluated to
            // undefined, so itemCount was silently always null here.
            const itemCount = updatedSession.rankingOutput
              ? updatedSession.rankingOutput.rankedItemIds.length
              : null;
            autoRecordDecision({
              source: 'session_published',
              workspaceId: getCurrentWorkspaceId() ?? '',
              actorId,
              sourceEventId: auditResult.id ?? null,
              sessionId,
              cycleId: session.cycleId ?? null,
              sessionName: session.name,
              itemCount,
              relatedObjects: [
                { kind: 'session', id: sessionId },
                ...(session.cycleId ? [{ kind: 'cycle' as const, id: session.cycleId }] : []),
              ],
            }).catch(() => {});
          }).catch(() => {});
        }

        // Auto-record decision: criteria finalized → selection decision (spec §3.2)
        if (targetState === 'CRITERIA_FINALIZED') {
          import('../../lib/decisionService').then(({ autoRecordDecision }) => {
            autoRecordDecision({
              source: 'criteria_finalized',
              workspaceId: getCurrentWorkspaceId() ?? '',
              actorId,
              sessionId,
              cycleId: session.cycleId ?? null,
              sessionName: session.name,
              // 985: why these criteria, written as they were locked.
              rationale: options?.rationale ?? null,
              relatedObjects: [
                { kind: 'session', id: sessionId },
                ...(session.cycleId ? [{ kind: 'cycle' as const, id: session.cycleId }] : []),
              ],
            }).catch(() => {});
          }).catch(() => {});
        }

        return { success: true };
      },
      
      canTransitionSessionState: (sessionId, targetState, options) => {
        const session = get().getSession(sessionId);
        if (!session) {
          return { allowed: false, reason: 'Session not found' };
        }
        
        const currentState = session.state;
        const participantCount = options?.participantCount ?? session.participantIds?.length ?? 0;
        
        // Define valid transitions (DRAFT may go to RUN_COMPLETE when track has locked criteria)
        const validTransitions: Record<SessionState, SessionState[]> = {
          DRAFT: ['CRITERIA_FINALIZED', 'RUN_COMPLETE'],
          CRITERIA_FINALIZED: ['DRAFT', 'RUN_COMPLETE'],
          RUN_COMPLETE: ['CRITERIA_FINALIZED', 'PUBLISHED'],
          PUBLISHED: [],
        };
        
        if (!validTransitions[currentState].includes(targetState)) {
          return { 
            allowed: false, 
            reason: `Cannot transition from ${currentState} to ${targetState}` 
          };
        }
        
        // Check guards for specific transitions
        if (currentState === 'DRAFT' && targetState === 'CRITERIA_FINALIZED') {
          if (sessionHasObviousOrLockedCriteria(session)) {
            // Built-in tool dimensions (Impact/Effort, RICE, etc.) or track-locked criteria — no picker step.
          } else {
            const hasSessionCriteria = session.criteriaConfig.criteriaIds.length > 0;
            if (!hasSessionCriteria && session.trackId) {
              const trackConfigs = useAttributeGovernanceStore.getState().getTrackConfigs(session.trackId);
              const trackHasAcceptedCriteria = trackConfigs.some(c => c.rangeStatus === 'accepted');
              if (!trackHasAcceptedCriteria) {
                return { allowed: false, reason: 'At least one criterion must be selected (on the session or the track)' };
              }
            } else if (!hasSessionCriteria) {
              return { allowed: false, reason: 'At least one criterion must be selected' };
            }
          }
        }
        
        // RUN_COMPLETE: allowed from CRITERIA_FINALIZED or from DRAFT when criteria are obvious/locked
        if (targetState === 'RUN_COMPLETE') {
          // V3 sessions frequently never set selectedToolId on the session row —
          // the tool lives on the active tool_session's toolType instead (see
          // LiveSessionPage.tsx effectiveToolId). Resolve it BEFORE the criteria
          // gate as well as the tool check: with selectedToolId null the gate
          // can't see that the tool defines/needs no criteria, and it then
          // refused to close a stack-ranking session on a criteria-locked track.
          const effectiveToolId = session.selectedToolId
            ?? useToolSessionStore.getState().toolSessions
              .filter(ts => ts.sessionId === sessionId)
              .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))[0]?.toolType
            ?? null;
          if (
            currentState === 'DRAFT'
            && !sessionHasObviousOrLockedCriteria({ ...session, selectedToolId: effectiveToolId })
          ) {
            return {
              allowed: false,
              reason: 'Criteria must be finalized before running (or use a track with locked criteria)',
            };
          }
          const eligibleItemIds = get().getEligibleItems(sessionId);
          if (eligibleItemIds.length === 0) {
            return { allowed: false, reason: 'At least one eligible item is required' };
          }
          if (!effectiveToolId) {
            return { allowed: false, reason: 'A prioritization tool must be selected' };
          }
          
          const items = useItemsStore.getState().items;
          const attributes = useAttributesStore.getState().attributes;
          const itemById = new Map(items.map(item => [item.id, item]));
          // V3/live-session item pools may include typed/ad-hoc items that were
          // never written to the workspace items table (only to session.itemIds
          // / v3Config.items / the tool_session snapshot — see getEligibleItems
          // above). Without a stand-in, evaluateToolCompatibility sees an empty
          // pool and reports "not enough items" even though the session has a
          // legitimate, non-empty eligible item list. Look up display titles from
          // any matching tool session snapshot for a better stand-in title.
          const toolSessionItemTitleById = new Map(
            useToolSessionStore.getState().toolSessions
              .filter(ts => ts.sessionId === sessionId)
              .flatMap(ts => ts.items ?? [])
              .map(i => [i.id, i.title])
          );
          const now = new Date();
          const eligibleItems: Item[] = eligibleItemIds.map(id => itemById.get(id) ?? {
            id,
            title: toolSessionItemTitleById.get(id) ?? id,
            lifecycle_state: 'item',
            createdAt: now,
            updatedAt: now,
            createdBy: session.facilitatorId,
            tags: [],
          });
          const effectiveCriteriaIds = session.criteriaConfig.criteriaIds.length > 0
            ? session.criteriaConfig.criteriaIds
            : (session.trackId ? useAttributeGovernanceStore.getState().getTrackConfigs(session.trackId).filter(c => c.rangeStatus === 'accepted').map(c => c.attributeKey) : []);
          const sessionCriteria = attributes.filter(attr => effectiveCriteriaIds.includes(attr.key));
          const valueProposalState = useValueProposalStore.getState();
          const valueSufficiency = computeValueSufficiency({
            itemIds: eligibleItemIds,
            attributeKeys: effectiveCriteriaIds,
            acceptedValues: valueProposalState.acceptedValues,
            exceptions: valueProposalState.exceptions,
            sessionId: session.id,
          });
          
          const canonicalToolId = normalizeSessionToolId(effectiveToolId) ?? effectiveToolId;
          const compatContext: ToolCompatibilityContext = {
            toolId: canonicalToolId,
            items: eligibleItems,
            criteria: sessionCriteria,
            poolLocked: true,
            valuesSufficient: valueSufficiency.valuesSufficient,
            criteriaLocked: true,
            valueCompletionByAttribute: valueSufficiency.valueCompletionByAttribute,
            minimumValueCompletionPercent: valueSufficiency.minimumValueCompletionPercent,
            participationLocked: true,
            participantCount,
          };
          
          const compatibility = evaluateToolCompatibility(compatContext);
          if (!compatibility.canUse) {
            const blockerSummary = compatibility.blockers.map(b => b.title).join('; ');
            return { 
              allowed: false, 
              reason: `Tool incompatible: ${blockerSummary}`,
            };
          }
        }
        
        if (currentState === 'RUN_COMPLETE' && targetState === 'PUBLISHED') {
          if (!session.rankingOutput) {
            return { allowed: false, reason: 'Ranking output is required before publishing' };
          }
          
          // Legacy gap repair: cycle-linked sessions created from the track flow can resolve
          // their iteration at publish time, but sessions without both track and iteration remain invalid.
          if (session.cycleId && !session.egapIterationId && !session.trackId) {
            return { 
              allowed: false, 
              reason: 'Session must belong to the required cycle-execution lineage before publishing',
            };
          }
        }
        
        return { allowed: true };
      },
      
      // ================================
      // Lock Semantics (v2.2 Canon)
      // ================================
      
      startSession: async (sessionId: string, actorId: string) => {
        const session = get().getSession(sessionId);
        if (!session) {
          return { success: false, error: 'Session not found' };
        }
        
        // Check if already locked
        if (session.configurationLockedAt) {
          return { 
            success: false, 
            error: 'Session configuration is already locked' 
          };
        }
        
        const effectiveMode: SessionContextMode = session.sessionContextMode ?? (session.trackId != null && session.trackId !== '' ? 'cycle_linked' : 'app_linked');
        if (effectiveMode === 'cycle_linked' && (session.trackId == null || session.trackId === '')) {
          return { success: false, error: 'Cycle-linked session must have a track' };
        }
        
        // Authorization check
        const policyDecision = checkPolicyForSession('session.facilitate', sessionId, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          return { success: false, error: `Authorization denied: ${reasonCode}` };
        }
        
        const now = new Date();
        let poolSnapshotId: string | undefined;
        let criteriaSnapshotId: string | undefined;
        let valuesSnapshotId: string | undefined;
        const participationSnapshotId = `participation-snapshot-${sessionId}-${now.getTime()}`;
        
        // Cycle-linked only: create track-dependent DB snapshots (pool, criteria, values).
        // App-linked/isolated: session-local lock only; no track-dependent snapshots.
        if (effectiveMode === 'cycle_linked' && session.trackId && isSupabaseAvailable()) {
          const { getSafeSupabaseClient } = await import('../../lib/supabaseSync');
          const supabase = getSafeSupabaseClient();
          
          if (supabase) {
            try {
              // 1. Pool Snapshot: Copy item pool state
              // NOTE: item_pools uses cycle_id, not track_id (per SUPABASE_SCHEMA_LIVE.md)
              const { data: pools } = await supabase
                .from('item_pools')
                .select('id, workspace_id, priority_ids, cut_line_position')
                .eq('cycle_id', session.cycleId);
              
              if (pools && pools.length > 0) {
                const pool = pools[0];
                const { data: poolSnapshot, error: poolError } = await supabase
                  .from('item_pool_snapshots')
                  .insert({
                    item_pool_id: pool.id,
                    // The snapshot belongs to the pool's workspace; a row with none is refused (migration 970).
                    workspace_id: pool.workspace_id,
                    priority_ids: pool.priority_ids || [],
                    cut_line_position: pool.cut_line_position,
                    reason: `Session ${sessionId} lock`,
                    version: 1,
                    created_by: actorId,
                    created_at: now.toISOString(),
                  })
                  .select('id')
                  .single();
                  
                if (poolError) {
                  console.error('[SessionStore] Failed to create pool snapshot:', poolError);
                } else {
                  poolSnapshotId = poolSnapshot?.id;
                }
              }
              
              // 2. Criteria Snapshot: Store criteria config state as JSON in session_reports
              const { data: criteriaConfigs } = await supabase
                .from('cycle_attribute_configs')
                .select('id, attribute_key, range_status')
                .eq('cycle_id', session.cycleId);
              
              if (criteriaConfigs && criteriaConfigs.length > 0) {
                criteriaSnapshotId = `criteria-snapshot-${sessionId}-${now.getTime()}`;
                
                const { error: criteriaError } = await supabase
                  .from('session_reports')
                  .insert({
                    session_id: sessionId,
                    track_id: session.trackId,
                    cycle_id: session.cycleId,
                    created_by: actorId,
                    intent: 'criteria_snapshot',
                    inputs_snapshot: { criteria: criteriaConfigs },
                    outputs_snapshot: {},
                    is_final: false,
                  });
                  
                if (criteriaError) {
                  console.error('[SessionStore] Failed to create criteria snapshot:', criteriaError);
                }
              }
              
              // 3. Values Snapshot: Store accepted values at lock time (track-scoped)
              const { data: values } = await supabase
                .from('value_proposals')
                .select('id, item_id, attribute_key, value, submitted_by')
                .eq('track_id', session.trackId)
                .eq('status', 'accepted');
              
              if (values && values.length > 0) {
                valuesSnapshotId = `values-snapshot-${sessionId}-${now.getTime()}`;
                
                const { error: valuesError } = await supabase
                  .from('session_reports')
                  .insert({
                    session_id: sessionId,
                    track_id: session.trackId,
                    cycle_id: session.cycleId,
                    created_by: actorId,
                    intent: 'values_snapshot',
                    inputs_snapshot: { values },
                    outputs_snapshot: {},
                    is_final: false,
                  });
                  
                if (valuesError) {
                  console.error('[SessionStore] Failed to create values snapshot:', valuesError);
                }
              }
              
            } catch (err) {
              console.error('[SessionStore] Error creating snapshots:', err);
            }
          }
        }
        
        // Fall back to generated IDs if database snapshots failed
        if (!poolSnapshotId) {
          poolSnapshotId = `pool-snapshot-${sessionId}-${now.getTime()}`;
        }
        if (!criteriaSnapshotId) {
          criteriaSnapshotId = `criteria-snapshot-${sessionId}-${now.getTime()}`;
        }
        
        const updatedSession = {
          ...session,
          configurationLockedAt: now,
          configurationLockedBy: actorId,
          poolSnapshotId,
          criteriaSnapshotId,
          participationSnapshotId,
          valuesSnapshotId,
          updatedAt: now,
        };
        
        // Update local state
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId ? updatedSession : s
          ),
        }));
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          const result = await get().syncToSupabase(updatedSession);
          if (result.error) {
            console.error('[SessionStore] Failed to sync session start to Supabase:', result.error);
          }
        }
        
        // Log audit event
        useAuditStore.getState().logSessionEvent({
          type: 'session_state_changed',
          sessionId,
          sessionName: session.name,
          actorId,
          actorRole: 'workspace-admin',
          details: {
            fromState: session.state,
            toState: updatedSession.state,
            poolSnapshotId,
            criteriaSnapshotId,
            participationSnapshotId,
            valuesSnapshotId,
          },
        });
        
        console.log('[SessionStore] Session started with locked configuration:', sessionId);
        
        return { 
          success: true, 
          poolSnapshotId,
          criteriaSnapshotId,
        };
      },
      
      isConfigurationLocked: (sessionId: string) => {
        const session = get().getSession(sessionId);
        return session?.configurationLockedAt != null;
      },
      
      getConfigurationLockStatus: (sessionId: string) => {
        const session = get().getSession(sessionId);
        if (!session) {
          return { locked: false };
        }
        
        return {
          locked: session.configurationLockedAt != null,
          lockedAt: session.configurationLockedAt,
          lockedBy: session.configurationLockedBy,
          poolSnapshotId: session.poolSnapshotId,
          criteriaSnapshotId: session.criteriaSnapshotId,
        };
      },
      
      // ================================
      // Participants
      // ================================
      
      addParticipant: (sessionId, userId) => {
        const session = get().getSession(sessionId);
        if (!session) return;

        // D01/D02: block participant add at RUN_COMPLETE or later. Canon §D01/D02.
        if (session.state === 'RUN_COMPLETE' || session.state === 'PUBLISHED') {
          console.warn('[SessionStore] addParticipant blocked: session is', session.state);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('participant-add-blocked', {
              detail: {
                store: 'session',
                action: 'addParticipant',
                sessionId,
                userId,
                state: session.state,
                message: `Cannot add participant: session is in ${session.state} state`,
              },
            }));
          }
          return;
        }

        const policyDecision = checkPolicyForSession('session.facilitate', sessionId, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for session.facilitate (addParticipant):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'addParticipant', message: `Cannot add participant: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId && !s.participantIds.includes(userId)
              ? { ...s, participantIds: [...s.participantIds, userId], updatedAt: new Date() }
              : s
          ),
        }));
      },
      
      removeParticipant: (sessionId, userId) => {
        const session = get().getSession(sessionId);
        if (!session) return;

        // D02: block participant remove at RUN_COMPLETE or PUBLISHED. Canon §D02.
        if (session.state === 'RUN_COMPLETE' || session.state === 'PUBLISHED') {
          console.warn('[SessionStore] removeParticipant blocked: session is', session.state);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('participant-remove-blocked', {
              detail: {
                store: 'session',
                action: 'removeParticipant',
                sessionId,
                userId,
                state: session.state,
                message: `Cannot remove participant: session is in ${session.state} state`,
              },
            }));
          }
          return;
        }

        const policyDecision = checkPolicyForSession('session.facilitate', sessionId, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for session.facilitate (removeParticipant):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'removeParticipant', message: `Cannot remove participant: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId
              ? { 
                  ...s, 
                  participantIds: s.participantIds.filter((id) => id !== userId),
                  updatedAt: new Date(),
                }
              : s
          ),
        }));
      },
      
      setFacilitator: (sessionId, userId) => {
        const session = get().getSession(sessionId);
        if (!session) return;
        const policyDecision = checkPolicyForSession('session.facilitate', sessionId, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for session.facilitate (setFacilitator):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'setFacilitator', message: `Cannot set facilitator: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId
              ? { 
                  ...s, 
                  facilitatorId: userId,
                  participantIds: s.participantIds.includes(userId) 
                    ? s.participantIds 
                    : [...s.participantIds, userId],
                  updatedAt: new Date(),
                }
              : s
          ),
        }));
      },
      
      // ================================
      // Criteria Configuration
      // ================================
      
      updateCriteriaConfig: (sessionId, config) => {
        const session = get().getSession(sessionId);
        if (!session) return;
        
        // Lock check: block post-lock modifications
        const lockCheck = isConfigModificationAllowed(session);
        if (!lockCheck.allowed) {
          console.warn('[SessionStore] Config locked (updateCriteriaConfig):', lockCheck.reason);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('configuration-locked', {
              detail: { store: 'session', action: 'updateCriteriaConfig', message: lockCheck.reason },
            }));
          }
          return;
        }
        
        const policyDecision = checkPolicyForSession('criteria.select', sessionId, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for criteria.select (updateCriteriaConfig):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'updateCriteriaConfig', message: `Cannot update criteria config: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        const updatedSession = {
          ...session,
          criteriaConfig: { ...session.criteriaConfig, ...config },
          updatedAt: new Date(),
        };
        
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId ? updatedSession : s
          ),
        }));
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          get().syncToSupabase(updatedSession);
        }
        
        // Recompute eligibility when criteria change
        get().recomputeAllEligibility(sessionId);
      },
      
      addCriterion: (sessionId, criterionId, _weight = 1) => {
        const session = get().getSession(sessionId);
        if (!session) return;
        
        // Lock check: block post-lock modifications
        const lockCheck = isConfigModificationAllowed(session);
        if (!lockCheck.allowed) {
          console.warn('[SessionStore] Config locked (addCriterion):', lockCheck.reason);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('configuration-locked', {
              detail: { store: 'session', action: 'addCriterion', message: lockCheck.reason },
            }));
          }
          return;
        }
        
        const policyDecision = checkPolicyForSession('criteria.select', sessionId, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for criteria.select (addCriterion):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'addCriterion', message: `Cannot add criterion: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        if (!session.criteriaConfig.criteriaIds.includes(criterionId)) {
          const newIds = [...session.criteriaConfig.criteriaIds, criterionId];
          const equalWeight = newIds.length > 0 ? 1 / newIds.length : 1;
          const normalizedWeights: Record<string, number> = {};
          for (const id of newIds) normalizedWeights[id] = equalWeight;

          set((state) => ({
            sessions: state.sessions.map((s) =>
              s.id === sessionId
                ? {
                    ...s,
                    criteriaConfig: {
                      ...s.criteriaConfig,
                      criteriaIds: newIds,
                      weights: normalizedWeights,
                    },
                    updatedAt: new Date(),
                  }
                : s
            ),
          }));

          const addedSession = get().getSession(sessionId);
          if (addedSession && isSupabaseAvailable()) get().syncToSupabase(addedSession);

          get().recomputeAllEligibility(sessionId);
        }
      },
      
      removeCriterion: (sessionId, criterionId) => {
        const session = get().getSession(sessionId);
        if (!session) return;
        
        // Lock check: block post-lock modifications
        const lockCheck = isConfigModificationAllowed(session);
        if (!lockCheck.allowed) {
          console.warn('[SessionStore] Config locked (removeCriterion):', lockCheck.reason);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('configuration-locked', {
              detail: { store: 'session', action: 'removeCriterion', message: lockCheck.reason },
            }));
          }
          return;
        }
        
        const policyDecision = checkPolicyForSession('criteria.select', sessionId, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for criteria.select (removeCriterion):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'removeCriterion', message: `Cannot remove criterion: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          sessions: state.sessions.map((s) => {
            if (s.id !== sessionId) return s;

            const { [criterionId]: _removed, ...remainingWeights } = s.criteriaConfig.weights;
            const remainingIds = s.criteriaConfig.criteriaIds.filter((id) => id !== criterionId);
            const sum = Object.values(remainingWeights).reduce((a, b) => a + b, 0);
            const normalizedWeights: Record<string, number> = {};
            if (sum > 0 && remainingIds.length > 0) {
              for (const id of remainingIds) normalizedWeights[id] = (remainingWeights[id] ?? 0) / sum;
            } else {
              const equal = remainingIds.length > 0 ? 1 / remainingIds.length : 0;
              for (const id of remainingIds) normalizedWeights[id] = equal;
            }

            return {
              ...s,
              criteriaConfig: {
                ...s.criteriaConfig,
                criteriaIds: remainingIds,
                weights: normalizedWeights,
                reconciliationRequiredSet: s.criteriaConfig.reconciliationRequiredSet.filter(
                  (id) => id !== criterionId
                ),
              },
              updatedAt: new Date(),
            };
          }),
        }));
        
        const removedSession = get().getSession(sessionId);
        if (removedSession && isSupabaseAvailable()) get().syncToSupabase(removedSession);

        get().recomputeAllEligibility(sessionId);
      },
      
      updateCriterionWeight: (sessionId, criterionId, weight) => {
        const session = get().getSession(sessionId);
        if (!session) return;
        
        // Per Track-Authoritative spec: Prevent mutation after session lock
        if (session.configurationLockedAt) {
          console.warn('[SessionStore] Cannot update criterion weight: session configuration is locked');
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'updateCriterionWeight', message: 'Session configuration is locked. Create a new session for changes.' },
            }));
          }
          return;
        }
        
        const policyDecision = checkPolicyForSession('criteria.weight', sessionId, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for criteria.weight (updateCriterionWeight):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'updateCriterionWeight', message: `Cannot update criterion weight: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        const clampedWeight = Math.max(0, Math.min(1, weight));
        const otherIds = session.criteriaConfig.criteriaIds.filter((id) => id !== criterionId);
        const remaining = 1 - clampedWeight;
        const oldOtherSum = otherIds.reduce((sum, id) => sum + (session.criteriaConfig.weights[id] ?? 0), 0);
        const normalizedWeights: Record<string, number> = { [criterionId]: clampedWeight };
        if (oldOtherSum > 0 && otherIds.length > 0) {
          for (const id of otherIds) {
            normalizedWeights[id] = ((session.criteriaConfig.weights[id] ?? 0) / oldOtherSum) * remaining;
          }
        } else if (otherIds.length > 0) {
          const equal = remaining / otherIds.length;
          for (const id of otherIds) normalizedWeights[id] = equal;
        }

        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId
              ? {
                  ...s,
                  criteriaConfig: {
                    ...s.criteriaConfig,
                    weights: normalizedWeights,
                  },
                  updatedAt: new Date(),
                }
              : s
          ),
        }));

        const weightedSession = get().getSession(sessionId);
        if (weightedSession && isSupabaseAvailable()) get().syncToSupabase(weightedSession);
      },
      
      setReconciliationRequired: (sessionId, attributeKeys) => {
        const session = get().getSession(sessionId);
        if (!session) return;
        
        // Per Track-Authoritative spec: Prevent mutation after session lock
        if (session.configurationLockedAt) {
          console.warn('[SessionStore] Cannot set reconciliation required: session configuration is locked');
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'setReconciliationRequired', message: 'Session configuration is locked. Create a new session for changes.' },
            }));
          }
          return;
        }
        
        const policyDecision = checkPolicyForSession('session.record', sessionId, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for session.record (setReconciliationRequired):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'setReconciliationRequired', message: `Cannot set reconciliation: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId
              ? {
                  ...s,
                  criteriaConfig: {
                    ...s.criteriaConfig,
                    reconciliationRequiredSet: attributeKeys,
                  },
                  updatedAt: new Date(),
                }
              : s
          ),
        }));
        
        get().recomputeAllEligibility(sessionId);
      },
      
      // ================================
      // Items (Session Pool)
      // ================================
      
      addItem: async (sessionId, itemId) => {
        const session = get().getSession(sessionId);
        if (!session) return { ok: false, reason: 'Session not found' };
        const existingIds = session.itemIds ?? [];
        if (existingIds.includes(itemId)) return { ok: true, alreadyIn: true };

        const lockCheck = isConfigModificationAllowed(session);
        if (!lockCheck.allowed) {
          console.warn('[SessionStore] Config locked (addItem):', lockCheck.reason);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('configuration-locked', {
              detail: { store: 'session', action: 'addItem', message: lockCheck.reason },
            }));
          }
          return { ok: false, reason: lockCheck.reason ?? 'Session is locked' };
        }

        const sessionData = {
          createdBy: session.createdBy,
          workspaceId: session.workspaceId,
          attributes: { facilitatorId: session.facilitatorId },
        };
        const policyDecision = checkPolicyForSession('session.pool.add', sessionId, sessionData);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for session.pool.add:', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'addItem', message: `Cannot add to session pool: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return { ok: false, reason: `Not allowed to add to this session (${reasonCode})` };
        }

        // SIMP enforcement: check whether any participant has already responded
        // to a tool_session linked to this prioritization_session.
        // Canon: 09A-session-edge-case-rules.md §A06, §B01
        const simpOutcome = await evaluateSimpForMutation(sessionId);
        if (simpOutcome === 'block') return { ok: false, reason: ITEMS_LOCKED_AFTER_VOTES };

        const eligibility = get().computeItemEligibility(sessionId, itemId);

        const updatedItemIds = [...existingIds, itemId];
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId
              ? {
                  ...s,
                  itemIds: updatedItemIds,
                  itemEligibility: {
                    ...(s.itemEligibility ?? {}),
                    [itemId]: eligibility,
                  },
                  updatedAt: new Date(),
                }
              : s
          ),
        }));

        get().recordDecision(sessionId, {
          type: 'candidate_inclusion',
          description: `Added item ${itemId}`,
          madeBy: [session.facilitatorId],
        });

        const updated = get().getSession(sessionId);
        if (!updated) return { ok: false, reason: 'Session not found' };
        const result = await get().syncToSupabase(updated);
        if (!result.success) {
          // Revert: restore previous itemIds and remove itemId from itemEligibility
          set((state) => ({
            sessions: state.sessions.map((s) =>
              s.id === sessionId
                ? {
                    ...s,
                    itemIds: existingIds,
                    itemEligibility: (() => {
                      const next = { ...(s.itemEligibility ?? {}) };
                      delete next[itemId];
                      return next;
                    })(),
                    updatedAt: new Date(),
                  }
                : s
            ),
          }));
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('session-sync-error', {
              detail: { store: 'session', action: 'addItem', message: result.error ?? 'Failed to save', sessionId },
            }));
          }
          return { ok: false, reason: result.error ?? 'Could not save the session' };
        }
        return { ok: true };
      },
      
      removeItem: async (sessionId, itemId) => {
        const session = get().getSession(sessionId);
        if (!session) return;
        
        // Lock check: block post-lock modifications to pool membership
        const lockCheck = isConfigModificationAllowed(session);
        if (!lockCheck.allowed) {
          console.warn('[SessionStore] Config locked (removeItem):', lockCheck.reason);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('configuration-locked', {
              detail: { store: 'session', action: 'removeItem', message: lockCheck.reason },
            }));
          }
          return;
        }
        
        const sessionData = {
          createdBy: session.createdBy,
          workspaceId: session.workspaceId,
          attributes: { facilitatorId: session.facilitatorId },
        };
        const policyDecision = checkPolicyForSession('session.pool.remove', sessionId, sessionData);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for session.pool.remove:', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'removeItem', message: `Cannot remove from session pool: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }

        // SIMP enforcement — removing an item from the pool after responses exist
        // is the same integrity concern as adding one. Canon: 09A §B02
        const simpOutcome = await evaluateSimpForMutation(sessionId);
        if (simpOutcome === 'block') {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('session-sync-error', {
              detail: { store: 'session', action: 'removeItem', message: ITEMS_LOCKED_AFTER_VOTES, sessionId },
            }));
          }
          return;
        }
        set((state) => ({
          sessions: state.sessions.map((s) => {
            if (s.id !== sessionId) return s;
            
            const { [itemId]: _removed, ...remainingEligibility } = s.itemEligibility ?? {};
            
            return {
              ...s,
              itemIds: (s.itemIds ?? []).filter((id) => id !== itemId),
              itemEligibility: remainingEligibility,
              updatedAt: new Date(),
            };
          }),
        }));
        
        get().recordDecision(sessionId, {
          type: 'candidate_exclusion',
          description: `Removed item ${itemId}`,
          madeBy: [session.facilitatorId],
        });

        const updated = get().getSession(sessionId);
        if (updated) get().syncToSupabase(updated);
      },
      
      addItems: async (sessionId, itemIds) => {
        const refuseAll = (reason: string) => ({ added: [] as string[], failed: itemIds.map((itemId) => ({ itemId, reason })) });
        const session = get().getSession(sessionId);
        if (!session) return refuseAll('Session not found');
        
        // Lock check: block post-lock modifications to pool membership
        const lockCheck = isConfigModificationAllowed(session);
        if (!lockCheck.allowed) {
          console.warn('[SessionStore] Config locked (addItems):', lockCheck.reason);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('configuration-locked', {
              detail: { store: 'session', action: 'addItems', message: lockCheck.reason },
            }));
          }
          return refuseAll(lockCheck.reason ?? 'Session is locked');
        }
        
        const sessionDataForPool = {
          createdBy: session.createdBy,
          workspaceId: session.workspaceId,
          facilitatorId: session.facilitatorId,
          attributes: { facilitatorId: session.facilitatorId },
        };
        const policyDecision = checkPolicyForSession('session.pool.add', sessionId, sessionDataForPool);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for session.pool.add (addItems batch):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'addItems', message: `Cannot add items: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return refuseAll(`Not allowed to add to this session (${reasonCode})`);
        }
        const added: string[] = [];
        const failed: Array<{ itemId: string; reason: string }> = [];
        for (const itemId of itemIds) {
          // One at a time: each add writes the whole item_ids array, and
          // concurrent writes would leave the last one to land as the truth.
          const outcome = await get().addItem(sessionId, itemId);
          if (outcome.ok) added.push(itemId);
          else failed.push({ itemId, reason: outcome.reason });
        }
        return { added, failed };
      },
      
      setSessionItemIds: (sessionId, itemIds) => {
        const session = get().getSession(sessionId);
        if (!session) return;
        const lockCheck = isConfigModificationAllowed(session);
        if (!lockCheck.allowed) return;
        const sessionData = {
          createdBy: session.createdBy,
          workspaceId: session.workspaceId,
          attributes: { facilitatorId: session.facilitatorId },
        };
        const policyDecision = checkPolicyForSession('session.pool.add', sessionId, sessionData);
        if (!policyDecision.allowed) return;
        const nextIds = [...new Set(itemIds)];
        const itemEligibility: Record<string, ItemEligibility> = { ...(session.itemEligibility ?? {}) };
        for (const itemId of nextIds) {
          if (!itemEligibility[itemId]) {
            itemEligibility[itemId] = get().computeItemEligibility(sessionId, itemId);
          }
        }
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId
              ? { ...s, itemIds: nextIds, itemEligibility, updatedAt: new Date() }
              : s
          ),
        }));
      },
      
      setIsolatedItems: (sessionId, items) => {
        const session = get().getSession(sessionId);
        if (!session) return;
        // The same rule as createSession: a typed line is identified by its text.
        const lines = uniqueLines(items);
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId
              ? { ...s, isolatedItems: lines, updatedAt: new Date() }
              : s
          ),
        }));
        const updated = get().getSession(sessionId);
        if (updated) get().syncToSupabase(updated);
      },

      computeItemEligibility: (sessionId, itemId) => {
        const session = get().getSession(sessionId);
        if (!session) {
          return {
            status: 'INELIGIBLE_STRUCTURAL' as ItemEligibilityStatus,
            missingAttributes: [],
            missingAcceptedValues: [],
            hasExceptions: false,
            acceptedExceptions: [],
            computedAt: new Date(),
          };
        }
        
        // Get the item
        const item = useItemsStore.getState().getItemById(itemId);
        if (!item) {
          return {
            status: 'INELIGIBLE_STRUCTURAL' as ItemEligibilityStatus,
            missingAttributes: ['Item not found'],
            missingAcceptedValues: [],
            hasExceptions: false,
            acceptedExceptions: [],
            computedAt: new Date(),
          };
        }
        
        // Check grid cell filter (altitude x item type)
        if (session.selectedGridCells && session.selectedGridCells.length > 0) {
          const itemLevel = item.altitudeLevel || 3; // Default to Level 3 if not set
          const itemType = item.itemCategory;

          const matchesFilter = session.selectedGridCells.some(
            cell => cell.altitudeLevel === itemLevel && cell.itemCategory === itemType
          );
          
          if (!matchesFilter) {
            return {
              status: 'INELIGIBLE_STRUCTURAL' as ItemEligibilityStatus,
              missingAttributes: ['Does not match altitude/type filter'],
              missingAcceptedValues: [],
              hasExceptions: false,
              acceptedExceptions: [],
              computedAt: new Date(),
            };
          }
        }
        
        // Get item's attributes from both item.attributes AND accepted values
        const valueProposalState = useValueProposalStore.getState();
        const itemAttributeKeys = (item as { attributes?: { attributeKey: string }[] }).attributes?.map((a) => a.attributeKey) || [];
        
        // Also consider accepted values as "having" the attribute
        // Try both with sessionId and without (global values)
        const acceptedValuesForItem = valueProposalState.getAcceptedValuesForItem(itemId, sessionId);
        const globalAcceptedValues = valueProposalState.getAcceptedValuesForItem(itemId); // Global values
        const allAcceptedValues = [...acceptedValuesForItem, ...globalAcceptedValues];
        const acceptedAttributeKeys = [...new Set(allAcceptedValues.map(av => av.attributeKey))];
        
        // Debug logging
        console.log(`[SessionStore] Eligibility check for ${itemId}:`, {
          itemTitle: item.title,
          sessionCriteria: session.criteriaConfig.criteriaIds,
          reconciliationRequired: session.criteriaConfig.reconciliationRequiredSet,
          itemAttributeKeys,
          acceptedAttributeKeys,
          totalAcceptedValues: valueProposalState.acceptedValues.length,
          acceptedValuesForThisItem: allAcceptedValues.length,
        });
        
        // Combine both sources
        const allAttributeKeys = new Set([...itemAttributeKeys, ...acceptedAttributeKeys]);
        
        // Check which criteria the item has
        const missingAttributes: string[] = [];
        for (const criterionId of session.criteriaConfig.criteriaIds) {
          if (!allAttributeKeys.has(criterionId)) {
            missingAttributes.push(criterionId);
          }
        }
        
        // Check which required values are missing
        const missingAcceptedValues: string[] = [];
        const acceptedExceptions: string[] = [];
        
        for (const attrKey of session.criteriaConfig.reconciliationRequiredSet) {
          // Check both session-scoped and global values
          const hasAccepted = !!valueProposalState.getAcceptedValue(itemId, attrKey, sessionId) ||
                              !!valueProposalState.getAcceptedValue(itemId, attrKey); // Also check global
          const hasException = valueProposalState.hasException(itemId, attrKey, sessionId);
          
          if (!hasAccepted && !hasException) {
            missingAcceptedValues.push(attrKey);
          } else if (hasException) {
            acceptedExceptions.push(attrKey);
          }
        }
        
        console.log(`[SessionStore] Eligibility result for ${itemId}:`, {
          missingAttributes,
          missingAcceptedValues,
          allAttributeKeys: [...allAttributeKeys],
        });
        
        const status = computeEligibilityStatus(
          missingAttributes,
          missingAcceptedValues,
          acceptedExceptions.length > 0
        );
        
        return {
          status,
          missingAttributes,
          missingAcceptedValues,
          hasExceptions: acceptedExceptions.length > 0,
          acceptedExceptions,
          computedAt: new Date(),
        };
      },
      
      recomputeAllEligibility: (sessionId) => {
        const session = get().getSession(sessionId);
        if (!session) return;
        
        const newEligibility: Record<string, ItemEligibility> = {};
        
        for (const itemId of session.itemIds) {
          newEligibility[itemId] = get().computeItemEligibility(sessionId, itemId);
        }
        
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId
              ? { ...s, itemEligibility: newEligibility, updatedAt: new Date() }
              : s
          ),
        }));
      },
      
      // ================================
      // Tool Configuration
      // ================================
      
      /**
       * Set the selected tool for a session.
       * Per Window 4: Validates tool compatibility before allowing selection.
       * Returns a result object indicating success/failure with blockers.
       */
      setSelectedTool: (sessionId, toolId, options) => {
        const session = get().getSession(sessionId);
        if (!session) return { success: false, error: 'Session not found' };
        const canonicalToolId = normalizeSessionToolId(toolId) ?? toolId;
        const participantCount = options?.participantCount ?? session.participantIds?.length ?? 0;
        
        // Lock check: block post-lock modifications
        const lockCheck = isConfigModificationAllowed(session);
        if (!lockCheck.allowed) {
          console.warn('[SessionStore] Config locked (setSelectedTool):', lockCheck.reason);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('configuration-locked', {
              detail: { store: 'session', action: 'setSelectedTool', message: lockCheck.reason },
            }));
          }
          return { success: false, error: lockCheck.reason };
        }
        
        const policyDecision = checkPolicyForSession('session.facilitate', sessionId, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for session.facilitate (setSelectedTool):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'setSelectedTool', message: `Cannot set tool: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return { success: false, error: reasonCode };
        }
        
        // Per Window 4: Evaluate tool compatibility before allowing selection
        // Get items and criteria for compatibility check
        const items = useItemsStore.getState().items;
        const attributes = useAttributesStore.getState().attributes;
        const eligibleItemIds = get().getEligibleItems(sessionId);
        const eligibleItems = items.filter(item => eligibleItemIds.includes(item.id));
        const effectiveCriteriaIds = session.criteriaConfig.criteriaIds.length > 0
          ? session.criteriaConfig.criteriaIds
          : (session.trackId ? useAttributeGovernanceStore.getState().getTrackConfigs(session.trackId).filter(c => c.rangeStatus === 'accepted').map(c => c.attributeKey) : []);
        const sessionCriteria = attributes.filter(attr => effectiveCriteriaIds.includes(attr.key));
        const criteriaLocked = session.state !== 'DRAFT' || (session.trackId ? useAttributeGovernanceStore.getState().getTrackConfigs(session.trackId).some(c => c.rangeStatus === 'accepted') : false);
        const valueProposalState = useValueProposalStore.getState();
        const valueSufficiency = computeValueSufficiency({
          itemIds: eligibleItemIds,
          attributeKeys: effectiveCriteriaIds,
          acceptedValues: valueProposalState.acceptedValues,
          exceptions: valueProposalState.exceptions,
          sessionId: session.id,
        });
        
        // Pool is locked if session advanced past DRAFT, OR if the track pool is
        // explicitly locked while still in DRAFT (covers pairwise ranking selection).
        const trackPoolForCompat = session.trackId
          ? useItemPoolsStore.getState().itemPools.find(p => p.trackId === session.trackId)
          : undefined;
        const poolLockedForCompat =
          session.state !== 'DRAFT' ||
          !!(trackPoolForCompat?.poolLockedAt);

        const compatContext: ToolCompatibilityContext = {
          toolId: canonicalToolId,
          items: eligibleItems,
          criteria: sessionCriteria,
          poolLocked: poolLockedForCompat,
          valuesSufficient: valueSufficiency.valuesSufficient,
          criteriaLocked,
          valueCompletionByAttribute: valueSufficiency.valueCompletionByAttribute,
          minimumValueCompletionPercent: valueSufficiency.minimumValueCompletionPercent,
          participationLocked: session.state !== 'DRAFT',
          participantCount,
        };

        const compatibility = evaluateToolCompatibility(compatContext);

        // In DRAFT, choosing a tool is configuration intent only — pool, criteria, and values
        // are still being set up. Full compatibility is enforced when transitioning to
        // RUN_COMPLETE (see canTransitionSessionState).
        if (session.state !== 'DRAFT' && !compatibility.canUse) {
          console.warn('[SessionStore] Tool incompatible:', compatibility.blockers);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('tool-incompatible', {
              detail: {
                store: 'session',
                action: 'setSelectedTool',
                toolId: canonicalToolId,
                blockers: compatibility.blockers,
                status: compatibility.status,
              },
            }));
          }
          return {
            success: false,
            error: 'Tool is incompatible with session configuration',
            blockers: compatibility.blockers.map((b) => b.title),
          };
        }

        const updatedSession = {
          ...session,
          selectedToolId: canonicalToolId,
          updatedAt: new Date(),
        };

        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId ? updatedSession : s
          ),
        }));

        if (isSupabaseAvailable()) {
          get().syncToSupabase(updatedSession);
        }

        const draftHints =
          session.state === 'DRAFT'
            ? [
                ...compatibility.blockers.map((b) => b.title),
                ...compatibility.warnings.map((w) => w.title),
              ].filter(Boolean)
            : [];

        return {
          success: true,
          warnings:
            session.state === 'DRAFT'
              ? draftHints
              : compatibility.warnings.map((w) => w.title),
        };
      },
      
      updateToolConfig: (sessionId, config) => {
        const session = get().getSession(sessionId);
        if (!session) return;
        
        // Lock check: block post-lock modifications
        const lockCheck = isConfigModificationAllowed(session);
        if (!lockCheck.allowed) {
          console.warn('[SessionStore] Config locked (updateToolConfig):', lockCheck.reason);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('configuration-locked', {
              detail: { store: 'session', action: 'updateToolConfig', message: lockCheck.reason },
            }));
          }
          return;
        }
        
        const policyDecision = checkPolicyForSession('session.facilitate', sessionId, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for session.facilitate (updateToolConfig):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'updateToolConfig', message: `Cannot update tool config: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId
              ? { ...s, toolConfig: { ...normalizeToolConfig(s.toolConfig), ...normalizeToolConfig(config) }, updatedAt: new Date() }
              : s
          ),
        }));
      },
      
      // ================================
      // Results
      // ================================
      
      setRankingOutput: (sessionId, output) => {
        const session = get().getSession(sessionId);
        if (!session) return;
        // E03: block ranking output writes on PUBLISHED sessions. Canon §E03.
        if (session.state === 'PUBLISHED') {
          console.warn('[SessionStore] setRankingOutput blocked: session is PUBLISHED');
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('published-session-mutation-blocked', {
              detail: { store: 'session', action: 'setRankingOutput', sessionId, message: 'Cannot modify ranking output: session is published.' },
            }));
          }
          return;
        }
        const policyDecision = checkPolicyForSession('session.vote', sessionId, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for session.vote (setRankingOutput):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'setRankingOutput', message: `Cannot set ranking output: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        const now = new Date();
        const fullOutput: SessionRankingOutput = {
          ...output,
          overrides: [],
          lastModifiedAt: now,
        };
        
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId
              ? { ...s, rankingOutput: fullOutput, updatedAt: now }
              : s
          ),
        }));
        
        // Log audit event
        useAuditStore.getState().logSessionEvent({
          type: 'session_tool_run',
          sessionId,
          sessionName: session.name,
          actorId: session.facilitatorId,
          actorRole: 'workspace-admin',
          details: {
            toolId: output.toolId,
            itemCount: output.rankedItemIds.length,
          },
        });
      },
      
      setCutLine: async (sessionId, position, rationale, rationaleParts) => {
        const session = get().getSession(sessionId);
        if (!session) return { success: false, error: 'Session not found' };
        // Cut lines are derived from results, never upfront config (Canon 23 D06):
        // only settable once a tool run has produced output, and not after publish.
        // The server enforces the same rule; this saves the round trip.
        if (session.state !== 'RUN_COMPLETE') {
          return {
            success: false,
            error: session.state === 'PUBLISHED'
              ? 'This session is published — its cut line can no longer be moved.'
              : 'The cut line can only be set after the session has been run.',
          };
        }
        if (session.cutLinePosition === position) return { success: true };

        // One governed transaction (set_session_cut_line, migration 749): the
        // server checks the facilitator / workspace admin, writes the position,
        // the session's decision log, a governance event and a range_selection
        // Decision together. Nothing changes here until it has.
        const result = await setSessionCutLine(sessionId, position, rationale, rationaleParts);
        if (!result.ok) {
          return { success: false, error: result.error ?? 'The cut line could not be saved.' };
        }
        const now = new Date();
        const actorId = useUserStore.getState().userId;
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId
              ? {
                  ...s,
                  cutLinePosition: position,
                  updatedAt: now,
                  decisions: [
                    ...(s.decisions ?? []),
                    {
                      id: typeof result.data?.decision_id === 'string' ? result.data.decision_id : `cut-line-${now.getTime()}`,
                      type: 'cut_line_set',
                      description: `Cut line set at position ${position}`,
                      madeBy: actorId ? [actorId] : [],
                      madeAt: now,
                      ...(rationale?.trim() ? { rationale: rationale.trim() } : {}),
                    },
                  ],
                }
              : s,
          ),
        }));
        return { success: true };
      },

      setResultingItemPool: (sessionId, itemPoolId) => {
        const session = get().getSession(sessionId);
        if (!session) return;
        const policyDecision = checkPolicyForSession('session.record', sessionId, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for session.record (setResultingItemPool):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'setResultingItemPool', message: `Cannot set resulting item pool: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId
              ? { ...s, resultingItemPoolId: itemPoolId, updatedAt: new Date() }
              : s
          ),
        }));
      },
      
      // ================================
      // Decisions
      // ================================
      
      recordDecision: (sessionId, decision) => {
        const session = get().getSession(sessionId);
        if (!session) return;
        const policyDecision = checkPolicyForSession('session.record', sessionId, session as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[SessionStore] Policy denied for session.record (recordDecision):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'session', action: 'recordDecision', message: `Cannot record decision: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        const fullDecision: SessionDecision = {
          ...decision,
          id: generateId(),
          madeAt: new Date(),
        };
        
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId
              ? { ...s, decisions: [...s.decisions, fullDecision], updatedAt: new Date() }
              : s
          ),
        }));
      },
      
      // ================================
      // Queries
      // ================================
      
      getSessionsForCycle: (cycleId) => {
        return get().sessions.filter((s) => s.cycleId === cycleId);
      },
      
      getStandaloneSessions: () => {
        return get().sessions.filter((s) => !s.cycleId);
      },
      
      getEligibleItems: (sessionId) => {
        const session = get().getSession(sessionId);
        if (!session) return [];

        // V3 Event Designer sessions frequently store their working item pool in
        // modeConfig.v3Config.items rather than session.itemIds (which stays empty).
        // Fall back to those ids so RUN_COMPLETE transitions (Close & save results)
        // don't block with "At least one eligible item is required" for a pool that
        // legitimately lives elsewhere. See LiveSessionPage.tsx sessionToolItems resolution.
        let itemIds = session.itemIds ?? [];
        if (itemIds.length === 0) {
          const v3Items = ((session.modeConfig as unknown as Record<string, unknown> | undefined)?.v3Config as
            | { items?: Array<{ id: string }> }
            | undefined)?.items ?? [];
          itemIds = v3Items.map((i) => i.id);
        }
        // Last resort: some live sessions only ever persist their item pool onto
        // the tool_session row snapshot (tool_sessions.items) — never onto
        // session.itemIds or v3Config.items. Without this fallback, "Close & save
        // results" incorrectly reports "no eligible items" for a session that
        // clearly has items with recorded participant responses (see
        // LiveSessionPage.tsx handleClose). Use the most recently created tool
        // session for this prioritization session that has an item snapshot.
        if (itemIds.length === 0) {
          const matchingToolSessions = useToolSessionStore.getState().toolSessions
            .filter(ts => ts.sessionId === sessionId && (ts.items?.length ?? 0) > 0)
            .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
          itemIds = (matchingToolSessions[0]?.items ?? []).map((i) => i.id);
        }

        const notStructurallyIneligible = (id: string) =>
          session.itemEligibility[id]?.status !== 'INELIGIBLE_STRUCTURAL';

        // Tools with built-in dimensions (Impact/Effort, RICE, etc.) collect scores in the tool;
        // itemEligibility is often never recomputed, so treat session items as runnable.
        if (sessionHasObviousOrLockedCriteria(session)) {
          return itemIds.filter(notStructurallyIneligible);
        }

        return itemIds.filter((id) => {
          const eligibility = session.itemEligibility[id];
          return eligibility?.status === 'ELIGIBLE';
        });
      },
      
      getItemsWithGaps: (sessionId) => {
        const session = get().getSession(sessionId);
        if (!session) return [];
        
        return session.itemIds.filter((id) => {
          const eligibility = session.itemEligibility[id];
          return eligibility?.status === 'ELIGIBLE_WITH_GAPS';
        });
      },
      
      getIneligibleItems: (sessionId) => {
        const session = get().getSession(sessionId);
        if (!session) return [];
        
        return session.itemIds.filter((id) => {
          const eligibility = session.itemEligibility[id];
          return eligibility?.status === 'INELIGIBLE_STRUCTURAL';
        });
      },
    
    // =========================================================================
    // Scale Readiness Enforcement
    // =========================================================================
    
    canCreateSessionWithIntent: (params) => {
      const blockers: string[] = [];
      
      // Define which intents require scale readiness
      const INTENTS_REQUIRING_SCALES: SessionIntent[] = [
        'converge_values',
        'rank_priorities',
        'select_item_pool',
      ];
      
      const requiresScales = INTENTS_REQUIRING_SCALES.includes(params.intent);
      
      // Check criteria lock for execution intents
      if (requiresScales && !params.criteriaLocked) {
        blockers.push('Criteria must be locked before creating this session');
      }
      
      // Check scale readiness for execution intents
      if (requiresScales && !params.criteriaScalesReady) {
        if (params.missingScales.length > 0) {
          if (params.missingScales.length <= 3) {
            blockers.push(
              `Scale not defined for: ${params.missingScales.map(s => `"${s}"`).join(', ')}`
            );
          } else {
            const firstThree = params.missingScales.slice(0, 3).map(s => `"${s}"`).join(', ');
            blockers.push(
              `Scale not defined for ${params.missingScales.length} criteria: ${firstThree}, and ${params.missingScales.length - 3} more`
            );
          }
        } else {
          blockers.push('All criteria scales must be defined and accepted');
        }
      }
      
      return {
        allowed: blockers.length === 0,
        blockers,
      };
    },
  })
);

// ============================================================================
// RPC-Based Session Operations (v3.0)
// ============================================================================

// createSessionViaRpc (create_session_for_track) was removed in 760: it had no
// caller and the RPC had no authority check (EXECUTE now revoked). Sessions are
// created through createSessionWithFeedback (src/lib/sessions/createSessionFlow.ts).

const SESSION_RESULTS_TABLE = 'session_results';

/** Ranking output shape used when persisting session_results. */
interface RankingOutputForPersist {
  rankedItemIds?: string[];
  orderedItems?: { id: string }[];
  scores?: Record<string, Record<string, number> & { total?: number }>;
  // Present on the real caller-supplied objects (see SessionRankingOutput in
  // types/prioritization/session.ts) — this local shape had dropped them
  // even though the function body below reads both. Value type kept as
  // `unknown` (rather than mirroring SessionRankingOutput's stricter
  // Record<string, Record<string,string>>) since the read below already
  // narrows defensively with typeof checks, and different tools produce
  // different classification-detail shapes.
  classifications?: Record<string, unknown>;
  toolId?: string;
}

/**
 * Everything that must happen once a session is RUN_COMPLETE, beyond the state
 * itself: the scoreboard event, the app telemetry signal, the first-run track
 * value lock and the session report.
 *
 * 771: this used to live inline in `transitionSessionState`, so which records a
 * finished run left behind depended on HOW it finished — a run through
 * `run_session` (the Run tab) produced no session report and never locked the
 * track's values, while a run that persisted results and then asked for the
 * state change did both. Same button, different durable reality. Both entries
 * call this now.
 */
async function applyRunCompleteSideEffects(
  session: PrioritizationSession,
  actorId?: string
): Promise<{ reportSaved: boolean }> {
  const eventWorkspaceId = session.workspaceId ?? getCurrentWorkspaceId() ?? null;

  if (eventWorkspaceId) {
    void useSessionStore.getState().writeSessionEvent({
      sessionId: session.id,
      workspaceId: eventWorkspaceId,
      actorId,
      actorName: '',
      eventType: 'result_locked',
      payload: {},
    });
    // FR-20: the Session app's terminal artifact was reached. Operational
    // signal only — tool id and item count, never item content.
    void appTelemetry.decisionCompleted('session', {
      toolId: session.selectedToolId,
      count: session.itemIds?.length ?? 0,
      state: 'RUN_COMPLETE',
    });
  }

  // Brief E: auto-lock track values on first RUN_COMPLETE for this track.
  if (session.trackId) {
    const workspaceId = getCurrentWorkspaceId();
    if (workspaceId) {
      // Phase C-1 (canon 28 §6): the lock stamp lives on session_baselines.
      const { data: trackForLock } = await (getSafeSupabaseClient()?.from('session_baselines')
        .select('values_converged_at')
        .eq('track_id', session.trackId)
        .maybeSingle() ?? Promise.resolve({ data: null }));
      if (trackForLock && !trackForLock.values_converged_at) {
        void import('./valueCollectionStore').then(({ lockTrackStandalone }) => {
          void lockTrackStandalone({
            trackId: session.trackId!,
            actorId: actorId ?? 'system',
            workspaceId,
            isAutomatic: true,
          });
        });
      }
    }
  }

  // Emit session report on completion (per D-002).
  const report = await useSessionReportStore.getState().createReport(session, actorId);
  if (!report && typeof window !== 'undefined') {
    const reportError = useSessionReportStore.getState().error;
    window.dispatchEvent(new CustomEvent('store-warning', {
      detail: {
        message: reportError
          ? `Session completed but the report could not be saved: ${reportError}`
          : 'Session state updated but report could not be saved. Completion may not be fully recorded.',
        type: 'error',
      },
    }));
    return { reportSaved: false };
  }
  if (report) {
    console.log('[SessionStore] Session report created:', report.id, 'intent:', report.intent);
  }
  return { reportSaved: true };
}

// ============================================================================
// Brief A — Result Status: publishSessionResult()
// ============================================================================

/**
 * Result surfacing status for a session_results row.
 * Append-only — never move backward.
 */
export type SessionResultStatus = 'draft' | 'complete' | 'published' | 'incorporated';

/**
 * Provenance data returned alongside result_status.
 * Populated from the session_results row (published_at/published_by)
 * and from the session row (facilitator, run date, item count, tool).
 */
export interface SessionResultProvenance {
  /** Timestamp the result was published (ISO string). */
  publishedAt: string | null;
  /** UUID of the user who published the result. */
  publishedBy: string | null;
  /** Number of result rows (= number of ranked items). */
  rankedItemCount: number;
}

/**
 * RETIRED (739) — `publishSessionResult()`, `publishSessionOutcome()` and
 * `publishSessionViaRpc()` used to live here. They were the three client-side
 * publish lanes, and which one a facilitator hit decided which durable records
 * their session ended up with. They are replaced by the single
 * `publishSessionResults()` below; do not reintroduce a parallel one.
 *
 * The server functions they wrapped are NOT retired: `publish_session_outcome`
 * and `publish_session_result` are what the one lane composes, and
 * `publish_session` still backs the public /v1 API. iOS calls the first two
 * directly and is unaffected.
 */

// ============================================================================
// The one publish lane — publishSessionResults()
// ============================================================================

/**
 * The single label for this act, everywhere it appears. "Publish results"
 * replaced five different ones (Publish Results / Publish results / Publish
 * Result / Publish priorities / Publish Session).
 */
export const PUBLISH_RESULTS_LABEL = 'Publish results';

/** The single confirmation, so six buttons cannot ask six different questions. */
export const PUBLISH_RESULTS_CONFIRM =
  'Publish these results?\n\n' +
  'This publishes the session result, records the governance events, writes the ' +
  'session report and notifies participants. When the session belongs to a cycle ' +
  'and a track it also ratifies and publishes the priority list and freezes the ' +
  'item pool ordering. It cannot be undone.';

/** What the session is expected to produce. Informational — the server decides. */
export type PublishSessionKind = 'priorities' | 'result';

export interface PublishSessionResultsOptions {
  /** Publishing actor. Omit to let the RPC bind to the authenticated principal. */
  actorId?: string;
  /** What the caller believes this publish produces. Never widens authority. */
  kind?: PublishSessionKind;
  /** Rationale recorded on the priority-set governance events. */
  rationale?: string;
  /** Message recorded on the publication event. */
  publicationMessage?: string;
  /** Set false to skip the shared confirmation (tests, already-confirmed flows). */
  confirm?: boolean;
}

export interface PublishSessionResultsOutcome {
  success: boolean;
  /** The server's own reason. Show it — never substitute a generic message. */
  error?: string;
  /** Machine code from the RPC (`not_authorized`, `not_run`, …), or `cancelled`. */
  code?: string;
  /** Which server lane ran: the full outcome, or the result on its own. */
  lane?: 'outcome' | 'result';
  alreadyPublished?: boolean;
  reportId?: string;
  prerequisiteBridged?: boolean;
  prioritySetId?: string;
  rankedCount?: number;
  resultsPublished?: number;
  snapshotVersion?: number | null;
  publishedAt?: string;
}

/**
 * Publish a session's results — the only path that may do so.
 *
 * Canon: publication is an authority transition (build contract §2.3a), and
 * Doc 01 §"Session Result Status" names the server lane: the publish RPC
 * "enforces all guards atomically: actor authorization, lifecycle state, and
 * result state are validated server-side in a single PostgreSQL transaction
 * that also writes the `governance_events` row."
 *
 * Every entry point calls this, and the same durable records come out every
 * time (migration 739 `publish_session_results`):
 *
 *   - session_results        complete → published
 *   - priority_sets          proposed → ratified → published, when the session
 *                            is bound to both a cycle and a track
 *   - governance_events      one per transition
 *   - item_pool_snapshots    ordering frozen, when the session produced a pool
 *   - session_reports        one row, never a second
 *   - catchball_level_turns  `ranking_session_published` prerequisite satisfied
 *   - session.state          PUBLISHED — which fires the 737 notification
 *                            trigger, so the client sends nothing itself
 *
 * A second publish is not an error: it returns success with
 * `alreadyPublished: true` and writes nothing new.
 */
export async function publishSessionResults(
  sessionId: string,
  options: PublishSessionResultsOptions = {}
): Promise<PublishSessionResultsOutcome> {
  if (options.confirm !== false && typeof window !== 'undefined' && typeof window.confirm === 'function') {
    if (!window.confirm(PUBLISH_RESULTS_CONFIRM)) {
      return { success: false, code: 'cancelled' };
    }
  }

  const supabase = getSafeSupabaseClient();
  if (!supabase) return { success: false, error: 'Supabase not available' };

  const { data, error } = await supabase.rpc('publish_session_results', {
    p_session_id: sessionId,
    p_actor_id: options.actorId ?? null,
    p_rationale: options.rationale ?? null,
    p_publication_message: options.publicationMessage ?? null,
  });

  if (error) {
    console.error('[SessionStore] publish_session_results RPC failed:', error);
    return { success: false, error: error.message };
  }

  const result = (data ?? null) as {
    success?: boolean;
    error?: string;
    code?: string;
    lane?: 'outcome' | 'result';
    already_published?: boolean;
    report_id?: string;
    prerequisite_bridged?: boolean;
    priority_set_id?: string | null;
    ranked_count?: number | null;
    results_published?: number | null;
    snapshot_version?: number | null;
    published_at?: string;
  } | null;

  if (!result?.success) {
    console.warn('[SessionStore] publish_session_results refused:', result?.error);
    return {
      success: false,
      error: result?.error ?? 'The server refused the publish',
      code: result?.code,
    };
  }

  // No optimistic transition (§2.3a): re-read the canonical row.
  await useSessionStore.getState().fetchSessionById(sessionId);

  // The prioritization memo is written when results are first published
  // (Brief K slice 3, owner ruling K9). In the background: publishing never
  // waits on it and never fails because of it. Loaded on demand so the store
  // does not pull the report and model code into every page.
  if (result.already_published !== true) {
    void import('../../lib/reportGenerationService')
      .then((m) => m.writePrioritizationMemo(sessionId, { actorId: options.actorId ?? null }))
      .then((r) => {
        if (!r.ok) console.warn('[SessionStore] prioritization memo not written:', r.error);
      })
      .catch((e) => console.warn('[SessionStore] prioritization memo not written:', e));
  }

  return {
    success: true,
    lane: result.lane,
    alreadyPublished: result.already_published === true,
    reportId: result.report_id,
    prerequisiteBridged: result.prerequisite_bridged === true,
    prioritySetId: result.priority_set_id ?? undefined,
    rankedCount: result.ranked_count ?? undefined,
    resultsPublished: result.results_published ?? undefined,
    snapshotVersion: result.snapshot_version ?? null,
    publishedAt: result.published_at,
  };
}

/**
 * Fetch result_status and provenance fields for a session from session_results.
 * Returns resultStatus = null when no rows exist yet (session never run).
 */
export async function fetchSessionResultStatus(
  sessionId: string
): Promise<{
  resultStatus: SessionResultStatus | null;
  provenance: SessionResultProvenance | null;
  error?: string;
}> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return { resultStatus: null, provenance: null, error: 'Supabase not available' };

  const { data, error } = await supabase
    .from(SESSION_RESULTS_TABLE)
    .select('result_status, published_at, published_by')
    .eq('session_id', sessionId)
    .limit(1)
    .maybeSingle();

  if (error) return { resultStatus: null, provenance: null, error: error.message };
  if (!data) return { resultStatus: null, provenance: null };

  // Count all rows to get ranked item count (avoid separate query — use count on the same table)
  const { count } = await supabase
    .from(SESSION_RESULTS_TABLE)
    .select('id', { count: 'exact', head: true })
    .eq('session_id', sessionId);

  const provenance: SessionResultProvenance = {
    publishedAt: data.published_at ?? null,
    publishedBy: data.published_by ?? null,
    rankedItemCount: count ?? 0,
  };

  return {
    resultStatus: (data.result_status as SessionResultStatus) ?? null,
    provenance,
  };
}

/**
 * Run a session via RPC (stores ranking results, transitions to RUN_COMPLETE).
 *
 * The RPC is the whole act (migration 760): in ONE transaction it writes
 * ranking_output, the RUN_COMPLETE state and the durable session_results rows
 * from p_rankings. There is no client write before or after it, so a refusal
 * (`success:false`, e.g. "Prerequisites not met") leaves nothing behind, and an
 * accepted run can never be missing its result rows.
 */
export async function runSessionViaRpc(
  sessionId: string,
  rankings: unknown,
  actorId?: string
): Promise<{ success: boolean; error?: string; readiness?: unknown }> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { success: false, error: 'Supabase not available' };
  }

  const session = useSessionStore.getState().getSession(sessionId);
  if (!session) {
    return { success: false, error: 'Session not found' };
  }
  const out = rankings as RankingOutputForPersist;
  const rankedCount = (out.rankedItemIds ?? out.orderedItems?.map((i) => i.id) ?? []).length;
  if (rankedCount === 0) {
    return { success: false, error: 'No ranked items to persist' };
  }

  try {
    const { data, error } = await supabase.rpc('run_session', {
      p_session_id: sessionId,
      p_rankings: rankings,
    });

    if (error) {
      if (error.message.includes('Could not find the function')) {
        console.debug('[SessionStore] run_session RPC not available');
        return { success: false, error: 'RPC not available' };
      }
      return { success: false, error: error.message };
    }

    if (!data?.success) {
      return { 
        success: false, 
        error: data?.error || 'Unknown error',
        readiness: data?.readiness,
      };
    }

    await useSessionStore.getState().fetchFromSupabase();

    // Safety net: if fetchFromSupabase returned a session without ranking_output
    // (e.g. DB write above was delayed or the RPC cleared it), restore from local.
    const refetched = useSessionStore.getState().getSession(sessionId);
    if (refetched && !refetched.rankingOutput) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      useSessionStore.getState().setRankingOutput(sessionId, out as any);
    }

    // The same records every other finished run leaves behind (771).
    await applyRunCompleteSideEffects(
      refetched ?? session,
      actorId ?? refetched?.facilitatorId ?? session.facilitatorId
    );

    // Brief F: broadcast item_scored events for each ranked item
    const workspaceId = getCurrentWorkspaceId();
    if (workspaceId && out.scores) {
      const rankedIds = out.rankedItemIds ?? out.orderedItems?.map((i) => i.id) ?? [];
      for (const itemId of rankedIds) {
        const itemScores = out.scores[itemId];
        if (itemScores) {
          void useSessionStore.getState().writeSessionEvent({
            sessionId,
            workspaceId,
            actorId: '',
            actorName: '',
            eventType: 'item_scored',
            payload: { item_id: itemId, score: itemScores.total ?? null, attribute_scores: itemScores },
          });
        }
      }
    }

    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    return { success: false, error: message };
  }
}

/**
 * Set track participation locked via RPC.
 */
export async function setTrackParticipationLockedViaRpc(
  trackId: string,
  locked: boolean
): Promise<{ success: boolean; error?: string }> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { success: false, error: 'Supabase not available' };
  }

  try {
    const { data, error } = await supabase.rpc('set_track_participation_locked', {
      p_track_id: trackId,
      p_locked: locked,
    });

    if (error) {
      if (error.message.includes('Could not find the function')) {
        return { success: false, error: 'RPC not available' };
      }
      return { success: false, error: error.message };
    }

    return { success: data?.success ?? false, error: data?.error };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    return { success: false, error: message };
  }
}

/**
 * Lock (or unlock) a track's criteria AND its scale ranges together — one
 * server transaction (set_track_criteria_and_ranges_locked, migration 756).
 * Owner ruling 2026-07-09: "Criteria & Scales" is one lock. Either half
 * refused → neither changes; there is no half-lock to undo.
 *
 * Migration 1005: each lock is a numbered version of the criteria; reopening
 * closes it. Reopening a version a session ran on needs `reason` (refused with
 * code `reason_required` without one).
 */
export async function setTrackCriteriaAndRangesLockedViaRpc(
  trackId: string,
  locked: boolean,
  reason?: string
): Promise<{ success: boolean; error?: string; code?: string; version?: number }> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { success: false, error: 'Supabase not available' };
  }

  try {
    const { data, error } = await supabase.rpc('set_track_criteria_and_ranges_locked', {
      p_track_id: trackId,
      p_locked: locked,
      ...(reason?.trim() ? { p_reason: reason.trim() } : {}),
    });

    if (error) {
      if (error.message.includes('Could not find the function')) {
        return { success: false, error: 'RPC not available' };
      }
      return { success: false, error: error.message };
    }

    const result = (data ?? {}) as { success?: boolean; error?: string; code?: string; version?: number };
    return { success: result.success ?? false, error: result.error, code: result.code, version: result.version };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    return { success: false, error: message };
  }
}

/**
 * Set track values converged via RPC.
 */
export async function setTrackValuesConvergedViaRpc(
  trackId: string,
  converged: boolean
): Promise<{ success: boolean; error?: string }> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { success: false, error: 'Supabase not available' };
  }

  try {
    const { data, error } = await supabase.rpc('set_track_values_converged', {
      p_track_id: trackId,
      p_converged: converged,
    });

    if (error) {
      if (error.message.includes('Could not find the function')) {
        return { success: false, error: 'RPC not available' };
      }
      return { success: false, error: error.message };
    }

    return { success: data?.success ?? false, error: data?.error };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    return { success: false, error: message };
  }
};

/**
 * Set track range-set locked via RPC.
 */
export async function setTrackRangesLockedViaRpc(
  trackId: string,
  locked: boolean
): Promise<{ success: boolean; error?: string }> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { success: false, error: 'Supabase not available' };
  }

  try {
    const { data, error } = await supabase.rpc('set_track_ranges_locked', {
      p_track_id: trackId,
      p_locked: locked,
    });

    if (error) {
      if (error.message.includes('Could not find the function')) {
        return { success: false, error: 'RPC not available' };
      }
      return { success: false, error: error.message };
    }

    return { success: data?.success ?? false, error: data?.error };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    return { success: false, error: message };
  }
}

// ============================================================================
// TV-Mode Presentation Data (Brief H)
// ============================================================================

export interface SessionPresentationData {
  session: {
    id: string;
    name: string;
    state: string;
    workspace_id: string;
    created_at: string | null;
  };
  rankedItems: Array<{
    rank: number;
    itemId: string;
    title: string;
    score: number | string | null;
  }>;
  sessionReport: { inputs_snapshot: unknown; created_at: string | null } | null;
  trackName: string | null;
  cycleName: string | null;
}

export type SessionPresentationResult =
  | { status: 'success'; data: SessionPresentationData }
  /** Session not accessible — not found or user not authorized (RLS). No data leaked. */
  | { status: 'not_found' }
  /** Session visible but result_status !== 'published'. No session name or items shown. */
  | { status: 'not_published' };

/**
 * Fetches all data needed to render the TV-mode presentation.
 *
 * Returns a discriminated union so the caller can show precise, safe error states:
 *   - not_found: session inaccessible (not found or unauthorized via RLS)
 *   - not_published: session visible but result not yet published
 *   - success: data ready to render
 *
 * No DB writes are performed — this is a read-only fetch.
 * Reconstruction is fully server-side — reads session_results directly, never from
 * cycle reports or in-memory state. Safe to call on every page load / refresh.
 */
export async function fetchSessionPresentationData(
  sessionId: string
): Promise<SessionPresentationResult> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return { status: 'not_found' };

  // 1. Session metadata — if null, RLS blocked it or it doesn't exist; both are not_found
  const { data: session } = await supabase
    .from('prioritization_sessions')
    .select('id, name, state, workspace_id, created_at')
    .eq('id', sessionId)
    .single();

  if (!session) return { status: 'not_found' };

  // 2. Guard: result must be published.
  // Empty resultRows and result_status !== 'published' both map to not_published — this
  // prevents enumerating whether a session has results at all.
  const { data: resultRows } = await supabase
    .from('session_results')
    .select('item_id, rank, score, result_status')
    .eq('session_id', sessionId)
    .order('rank', { ascending: true });

  if (!resultRows?.length || resultRows[0].result_status !== 'published') {
    return { status: 'not_published' };
  }

  // 3. Item titles
  const itemIds = resultRows.map((r: { item_id: string }) => r.item_id);
  const { data: items } = await supabase
    .from('items')
    .select('id, title')
    .in('id', itemIds);

  const itemMap = new Map<string, string>(
    (items ?? []).map((i: { id: string; title: string }) => [i.id, i.title])
  );

  // 4. Session report snapshot (for cut line and published timestamp)
  const { data: sessionReport } = await supabase
    .from('session_reports')
    .select('inputs_snapshot, created_at')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  // 5. Track and cycle context (best-effort — standalone sessions have no track)
  let trackName: string | null = null;
  let cycleName: string | null = null;
  try {
    const { data: trackAndCycle } = await supabase
      .from('prioritization_sessions')
      .select(`
        track_id,
        tracks ( name, cycle_id,
          degap_cycles ( name )
        )
      `)
      .eq('id', sessionId)
      .single();

    if (trackAndCycle?.tracks) {
      const t = trackAndCycle.tracks as { name?: string; degap_cycles?: { name?: string } | null };
      trackName = t.name ?? null;
      cycleName = t.degap_cycles?.name ?? null;
    }
  } catch {
    // standalone session — no track/cycle context; ok to continue
  }

  return {
    status: 'success',
    data: {
      session,
      rankedItems: resultRows.map((r: { rank: number; item_id: string; score: number | string | null }) => ({
        rank: r.rank,
        itemId: r.item_id,
        title: itemMap.get(r.item_id) ?? '(unknown)',
        score: r.score,
      })),
      sessionReport: sessionReport ?? null,
      trackName,
      cycleName,
    },
  };
}

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * Get all sessions
 */
export const useAllSessions = () => useSessionStore((state) => state.sessions);

/**
 * Get active session
 */
export const useActiveSession = () => useSessionStore((state) => state.getActiveSession());

/**
 * Get sessions for a cycle.
 * Selects state.sessions (stable ref) and filters in useMemo so getSnapshot is cached.
 */
export const useCycleSessions = (cycleId: string) => {
  const sessions = useSessionStore((state) => state.sessions);
  return useMemo(
    () => sessions.filter((s) => s.cycleId === cycleId),
    [sessions, cycleId]
  );
};

/**
 * Get standalone sessions
 */
export const useStandaloneSessions = () => 
  useSessionStore((state) => state.getStandaloneSessions());

/**
 * Get sessions for a track (Track-Authoritative).
 * Per spec: All prioritization queries must filter by track_id.
 */
export const useTrackSessions = (trackId: string) => {
  const sessions = useSessionStore((state) => state.sessions);
  return useMemo(
    () => sessions.filter((s) => s.trackId === trackId),
    [sessions, trackId]
  );
};
